// PROA_TRIAL_30D_UNIFIED_V1
// frontend/src/pages/LandingPage.tsx
// CLEAN BUILD V6.1 - FIXED HERO (RESTORED) + NEW PRODUCT & ORIGIN NARRATIVE
import React, { useEffect, useRef, useState } from 'react';
import {
    Box, Typography, Paper, useTheme, useMediaQuery,
    alpha, Container, Stack, Grid, Button, Divider, TextField, MenuItem, Chip, Switch
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { usePublicLeadForm } from '../hooks/usePublicLeadForm';

// Iconos Originales + Nuevos para Sección Producto
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';
import EmailIcon from '@mui/icons-material/Email';

// Iconos técnicos reutilizados en la landing
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import MemoryIcon from '@mui/icons-material/Memory';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

import { motion, useInView, useSpring, useMotionValue } from "framer-motion";

// const HERO_IMAGE_URL = 'https://deeygnrvxqakfppbrnwj.supabase.co/storage/v1/object/public/assets//pexels-joshsorenson-1054391.jpg';
// const AVATAR_PLACEHOLDER = 'https://ui-avatars.com/api/?name=Founder&background=fcd34d&color=0f172a&size=200';

// --- COMPONENTE DE CONTADOR ANIMADO ---
const AnimatedCounter = ({
    value,
    decimals = 0,
}: {
    value: number;
    decimals?: number;
}) => {
    const ref = useRef<HTMLSpanElement>(null);
    const motionValue = useMotionValue(0);
    const springValue = useSpring(motionValue, {
        damping: 50,
        stiffness: 100,
    });
    const isInView = useInView(ref, {
        once: false,
        margin: "-10px",
    });

    const formatter = React.useMemo(
        () =>
            new Intl.NumberFormat('es-ES', {
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }),
        [decimals]
    );

    useEffect(() => {
        motionValue.set(isInView ? value : 0);
    }, [isInView, value, motionValue]);

    useEffect(() => {
        return springValue.on("change", (latest) => {
            if (ref.current) {
                ref.current.textContent = formatter.format(latest);
            }
        });
    }, [springValue, formatter]);

    return <span ref={ref} />;
};

// --- WRAPPER DE ANIMACIÓN ---
const RevealSection = ({ children, id, delay = 0 }: { children: React.ReactNode, id?: string, delay?: number }) => {
    return (
        <motion.div
            id={id}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.2 }}
            transition={{ duration: 0.8, delay, ease: "easeOut" }}
            style={{ height: '100%', width: '100%' }} // <--- ESTA LÍNEA ES LA CLAVE
        >
            {children}
        </motion.div>
    );
};

const PricingHeroPreview = () => {
    return (
        <Paper
            elevation={0}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                maxWidth: 590,
                mx: 'auto',
                p: { xs: 2.2, sm: 2.8, md: 3.2 },
                borderRadius: 4,
                bgcolor: alpha('#0f172a', 0.78),
                border: `1px solid ${alpha('#fcd34d', 0.18)}`,
                backdropFilter: 'blur(18px)',
                boxShadow: `0 32px 100px ${alpha('#020617', 0.48)}`,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.08,
                    pointerEvents: 'none',
                    backgroundImage: `
                        linear-gradient(rgba(148,163,184,0.12) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(148,163,184,0.12) 1px, transparent 1px)
                    `,
                    backgroundSize: '36px 36px',
                }}
            />

            <Stack
                spacing={2.4}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                >
                    <Box>
                        <Typography
                            sx={{
                                color: '#fcd34d',
                                fontSize: '0.67rem',
                                fontWeight: 800,
                                letterSpacing: 1.7,
                            }}
                        >
                            PREVISIÓN CLIENTE-PRODUCTO
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.35,
                                color: '#f8fafc',
                                fontSize: '0.96rem',
                                fontWeight: 700,
                            }}
                        >
                            Cliente 024 · Referencia 0421
                        </Typography>
                    </Box>

                    <Typography
                        sx={{
                            px: 1.1,
                            py: 0.5,
                            borderRadius: 999,
                            color: '#94a3b8',
                            bgcolor: alpha('#94a3b8', 0.07),
                            border: `1px solid ${alpha('#94a3b8', 0.14)}`,
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: 0.8,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        DEMO · DATOS FICTICIOS
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        position: 'relative',
                        height: { xs: 150, md: 175 },
                        borderTop: `1px solid ${alpha('#94a3b8', 0.1)}`,
                        borderBottom: `1px solid ${alpha('#94a3b8', 0.1)}`,
                        background: `
                            linear-gradient(
                                180deg,
                                ${alpha('#38bdf8', 0.025)} 0%,
                                transparent 100%
                            )
                        `,
                    }}
                >
                    <Box
                        component="svg"
                        viewBox="0 0 340 140"
                        preserveAspectRatio="none"
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            overflow: 'visible',
                        }}
                    >
                        <defs>
                            <linearGradient
                                id="pricingForecastArea"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor="#38bdf8"
                                    stopOpacity="0.18"
                                />
                                <stop
                                    offset="100%"
                                    stopColor="#38bdf8"
                                    stopOpacity="0"
                                />
                            </linearGradient>
                        </defs>

                        <path
                            d="M0 108 C45 98 64 72 108 79 C150 86 165 108 204 86 C245 63 275 42 340 58 L340 140 L0 140 Z"
                            fill="url(#pricingForecastArea)"
                        />

                        <motion.path
                            d="M0 108 C45 98 64 72 108 79 C150 86 165 108 204 86 C245 63 275 42 340 58"
                            fill="none"
                            stroke="#38bdf8"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            initial={{ pathLength: 0, opacity: 0 }}
                            whileInView={{ pathLength: 1, opacity: 1 }}
                            viewport={{ once: false, amount: 0.5 }}
                            transition={{
                                duration: 1.25,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                        />

                        <circle
                            cx="340"
                            cy="58"
                            r="4"
                            fill="#fcd34d"
                        />
                    </Box>

                    <Typography
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 12,
                            color: '#64748b',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            letterSpacing: 1,
                        }}
                    >
                        PREVISIÓN
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: 'repeat(3, 1fr)',
                        },
                        borderTop: `1px solid ${alpha('#94a3b8', 0.1)}`,
                    }}
                >
                    {[
                        {
                            label: 'SIGUIENTE PERIODO',
                            value: '1.240 uds',
                            color: '#fcd34d',
                        },
                        {
                            label: 'MARGEN RECOMENDADO',
                            value: '18,5 %',
                            color: '#f8fafc',
                        },
                        {
                            label: 'CONFIANZA',
                            value: 'ALTA',
                            color: '#38bdf8',
                        },
                    ].map((item, index) => (
                        <Box
                            key={item.label}
                            sx={{
                                pt: 2,
                                pb: { xs: 1.5, sm: 0.5 },
                                pr: { sm: 2 },
                                pl: { sm: index === 0 ? 0 : 2 },
                                borderLeft: {
                                    xs: 'none',
                                    sm:
                                        index === 0
                                            ? 'none'
                                            : `1px solid ${alpha('#94a3b8', 0.1)}`,
                                },
                                borderBottom: {
                                    xs:
                                        index === 2
                                            ? 'none'
                                            : `1px solid ${alpha('#94a3b8', 0.08)}`,
                                    sm: 'none',
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.61rem',
                                    fontWeight: 700,
                                    letterSpacing: 1.05,
                                }}
                            >
                                {item.label}
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.65,
                                    color: item.color,
                                    fontSize: '1.28rem',
                                    fontWeight: 800,
                                }}
                            >
                                {item.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Stack>
        </Paper>
    );
};

const PricingDecisionSurface = () => {
    const metrics = [
        {
            label: 'DEMANDA PREVISTA',
            value: 12480,
            decimals: 0,
            suffix: ' uds',
            text: 'Previsión mensual agregada.',
            accent: '#38bdf8',
        },
        {
            label: 'MARGEN RECOMENDADO',
            value: 18.5,
            decimals: 1,
            suffix: ' %',
            text: 'Margen aplicado sobre coste.',
            accent: '#fcd34d',
        },
        {
            label: 'PRECIO DERIVADO',
            value: 42.6,
            decimals: 2,
            suffix: ' €',
            text: 'Derivado del coste y el margen recomendado.',
            accent: '#fcd34d',
        },
    ];

    return (
        <Box
            id="pricing-reading"
            sx={{
                position: 'relative',
                width: '100%',
                py: { xs: 9, md: 12 },
                bgcolor: '#081120',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.08,
                    pointerEvents: 'none',
                    backgroundImage: `
                        linear-gradient(rgba(148,163,184,0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(148,163,184,0.1) 1px, transparent 1px)
                    `,
                    backgroundSize: '64px 64px',
                }}
            />

            <Container
                maxWidth="xl"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <RevealSection>
                    <Box
                        sx={{
                            maxWidth: 760,
                            mb: { xs: 4, md: 5.5 },
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#fcd34d',
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                letterSpacing: 1.8,
                            }}
                        >
                            LECTURA COMERCIAL
                        </Typography>

                        <Typography
                            component="h2"
                            sx={{
                                mt: 1.6,
                                color: '#f8fafc',
                                fontWeight: 700,
                                fontSize: {
                                    xs: '2rem',
                                    md: '2.8rem',
                                },
                                lineHeight: 1.12,
                                letterSpacing: '-0.035em',
                            }}
                        >
                            De la previsión al contexto de una decisión.
                        </Typography>

                        <Typography
                            sx={{
                                mt: 2,
                                maxWidth: 690,
                                color: '#94a3b8',
                                fontSize: {
                                    xs: '1rem',
                                    md: '1.08rem',
                                },
                                lineHeight: 1.7,
                            }}
                        >
                            Un mismo contexto cliente-producto conecta demanda
                            esperada, margen recomendado, precio derivado y nivel
                            de confianza.
                        </Typography>
                    </Box>
                </RevealSection>

                <RevealSection delay={0.08}>
                    <Paper
                        elevation={0}
                        sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            bgcolor: alpha('#0f172a', 0.72),
                            border: `1px solid ${alpha('#fcd34d', 0.16)}`,
                            borderRadius: 4,
                            boxShadow: `0 24px 80px ${alpha('#020617', 0.3)}`,
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={1}
                            sx={{
                                px: { xs: 2.4, md: 3.2 },
                                py: 1.6,
                                borderBottom: `1px solid ${alpha('#94a3b8', 0.11)}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#cbd5e1',
                                    fontSize: '0.68rem',
                                    fontWeight: 800,
                                    letterSpacing: 1.4,
                                }}
                            >
                                CONTEXTO CLIENTE-PRODUCTO
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.63rem',
                                    fontWeight: 700,
                                    letterSpacing: 0.9,
                                }}
                            >
                                DEMO DE PRODUCTO · DATOS FICTICIOS
                            </Typography>
                        </Stack>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    md: 'repeat(4, 1fr)',
                                },
                            }}
                        >
                            {metrics.map((metric) => (
                                <Box
                                    key={metric.label}
                                    sx={{
                                        minHeight: { md: 215 },
                                        p: {
                                            xs: 2.6,
                                            md: 3.2,
                                        },
                                        borderRight: {
                                            xs: 'none',
                                            md: `1px solid ${alpha('#94a3b8', 0.1)}`,
                                        },
                                        borderBottom: {
                                            xs: `1px solid ${alpha('#94a3b8', 0.1)}`,
                                            md: 'none',
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                bgcolor: metric.accent,
                                                boxShadow: `0 0 12px ${alpha(
                                                    metric.accent,
                                                    0.55
                                                )}`,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                color: '#94a3b8',
                                                fontSize: '0.67rem',
                                                fontWeight: 800,
                                                letterSpacing: 1.2,
                                            }}
                                        >
                                            {metric.label}
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        sx={{
                                            mt: 3,
                                            color: '#f8fafc',
                                            fontSize: {
                                                xs: '2.5rem',
                                                md: '2.8rem',
                                            },
                                            fontWeight: 800,
                                            lineHeight: 1,
                                            letterSpacing: '-0.045em',
                                        }}
                                    >
                                        <AnimatedCounter
                                            value={metric.value}
                                            decimals={metric.decimals}
                                        />
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                color: metric.accent,
                                                fontSize: '0.48em',
                                                fontWeight: 700,
                                                letterSpacing: 0,
                                            }}
                                        >
                                            {metric.suffix}
                                        </Box>
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 2,
                                            color: '#64748b',
                                            fontSize: '0.86rem',
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        {metric.text}
                                    </Typography>
                                </Box>
                            ))}

                            <Box
                                sx={{
                                    minHeight: { md: 215 },
                                    p: {
                                        xs: 2.6,
                                        md: 3.2,
                                    },
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: '#38bdf8',
                                            boxShadow: `0 0 12px ${alpha(
                                                '#38bdf8',
                                                0.55
                                            )}`,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            color: '#94a3b8',
                                            fontSize: '0.67rem',
                                            fontWeight: 800,
                                            letterSpacing: 1.2,
                                        }}
                                    >
                                        CONFIANZA
                                    </Typography>
                                </Stack>

                                <Typography
                                    sx={{
                                        mt: 3,
                                        color: '#38bdf8',
                                        fontSize: {
                                            xs: '2.5rem',
                                            md: '2.8rem',
                                        },
                                        fontWeight: 800,
                                        lineHeight: 1,
                                        letterSpacing: '-0.045em',
                                    }}
                                >
                                    ALTA
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 2,
                                        color: '#64748b',
                                        fontSize: '0.86rem',
                                        lineHeight: 1.55,
                                    }}
                                >
                                    Nivel orientativo para interpretar la
                                    previsión antes de decidir.
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </RevealSection>
            </Container>
        </Box>
    );
};

const PricingProductStory = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [activeStep, setActiveStep] = useState(0);

    const stories = [
        {
            eyebrow: '01 · ANTICIPAR',
            title: 'Previsión mensual a nivel cliente-producto.',
            paragraphs: [
                'A partir del histórico comercial, PROAPricing estima las unidades esperadas para los próximos periodos con granularidad cliente-producto.',
                'El horizonte puede ser de tres o doce meses según el plan contratado.',
            ],
            ghost: 'DEMANDA',
        },
        {
            eyebrow: '02 · PREPARAR',
            title: 'Una recomendación de margen para cada operación.',
            paragraphs: [
                'Cliente, producto, coste, cantidad y estrategia aportan el contexto de partida.',
                'PROAPricing recomienda un margen aplicado sobre coste y deriva un precio de venta, incorporando información adicional para interpretar la recomendación.',
            ],
            ghost: 'MARGEN',
        },
        {
            eyebrow: '03 · COMPARAR',
            title: 'Explora cómo cambia la decisión cuando cambia el volumen.',
            paragraphs: [
                'Una recomendación no tiene por qué interpretarse como una única respuesta.',
                'Los escenarios por cantidad permiten comparar distintas condiciones de margen y precio antes de llevarlas a una negociación.',
            ],
            ghost: 'ESCENARIOS',
        },
        {
            eyebrow: '04 · ESCALAR',
            title: 'Trabaja sobre una cartera compleja sin perder trazabilidad.',
            paragraphs: [
                'Las optimizaciones pueden realizarse individualmente o por lotes y conservarse para revisión posterior.',
                'Advertencias, explicaciones e historial ayudan a mantener contexto sobre cada análisis. La decisión final permanece siempre en el usuario.',
            ],
            ghost: 'CONTROL',
        },
    ];

    const canvasBaseSx = {
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        borderRadius: 4,
        bgcolor: '#0b1424',
        border: `1px solid ${alpha('#94a3b8', 0.16)}`,
        boxShadow: `0 28px 90px ${alpha('#020617', 0.28)}`,
        backgroundImage: `
            linear-gradient(rgba(148,163,184,0.045) 1px, transparent 1px),
            linear-gradient(90deg, rgba(148,163,184,0.045) 1px, transparent 1px)
        `,
        backgroundSize: '36px 36px',
    } as const;

    const DemoLabel = () => (
        <Typography
            sx={{
                px: 1.05,
                py: 0.45,
                borderRadius: 999,
                color: '#64748b',
                bgcolor: alpha('#94a3b8', 0.055),
                border: `1px solid ${alpha('#94a3b8', 0.12)}`,
                fontSize: '0.58rem',
                fontWeight: 800,
                letterSpacing: 0.8,
                whiteSpace: 'nowrap',
            }}
        >
            DEMO · DATOS FICTICIOS
        </Typography>
    );

    const renderScene = (step: number, compact = false) => {
        if (step === 0) {
            const series = [
                {
                    month: 'AGO',
                    value: '980 uds',
                    type: 'historical',
                },
                {
                    month: 'SEP',
                    value: '1.060 uds',
                    type: 'historical',
                },
                {
                    month: 'OCT',
                    value: '1.120 uds',
                    type: 'forecast',
                },
                {
                    month: 'NOV',
                    value: '1.240 uds',
                    type: 'forecast',
                    highlighted: true,
                },
                {
                    month: 'DIC',
                    value: '1.180 uds',
                    type: 'forecast',
                },
            ];

            return (
                <Box
                    sx={{
                        ...canvasBaseSx,
                        minHeight: compact ? 500 : 560,
                        p: {
                            xs: 2,
                            sm: 2.6,
                            lg: 3,
                        },
                    }}
                >
                    <Typography
                        aria-hidden="true"
                        sx={{
                            position: 'absolute',
                            right: -12,
                            top: 58,
                            color: alpha('#38bdf8', 0.035),
                            fontSize: compact ? '4.5rem' : '6.5rem',
                            fontWeight: 800,
                            letterSpacing: '-0.07em',
                            userSelect: 'none',
                            pointerEvents: 'none',
                        }}
                    >
                        DEMANDA
                    </Typography>

                    {/* Cabecera de módulo */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    color: '#38bdf8',
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    letterSpacing: 1.5,
                                }}
                            >
                                PREVISIÓN DE DEMANDA
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.35,
                                    color: '#f8fafc',
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                }}
                            >
                                Análisis cliente-producto
                            </Typography>
                        </Box>

                        <DemoLabel />
                    </Stack>

                    {/* Contexto de consulta */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr 1fr',
                                sm: '1.1fr 1.1fr 0.9fr 0.9fr',
                            },
                            gap: 1,
                            mt: 2.3,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        {[
                            ['CLIENTE', 'C-024'],
                            ['PRODUCTO', 'P-0421'],
                            ['GRANULARIDAD', 'Mensual'],
                            ['HORIZONTE', '3 meses'],
                        ].map(([label, value], index) => (
                            <motion.div
                                key={label}
                                initial={{
                                    opacity: 0,
                                    y: 8,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{
                                    duration: 0.34,
                                    delay: 0.04 + index * 0.05,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        px: 1.35,
                                        py: 1.05,
                                        borderRadius: 2.2,
                                        bgcolor:
                                            label === 'HORIZONTE'
                                                ? alpha('#fcd34d', 0.045)
                                                : alpha('#0f172a', 0.48),
                                        border: `1px solid ${
                                            label === 'HORIZONTE'
                                                ? alpha('#fcd34d', 0.16)
                                                : alpha('#94a3b8', 0.11)
                                        }`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.54rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.9,
                                        }}
                                    >
                                        {label}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.4,
                                            color:
                                                label === 'HORIZONTE'
                                                    ? '#fcd34d'
                                                    : '#e2e8f0',
                                            fontSize: '0.73rem',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            </motion.div>
                        ))}
                    </Box>

                    {/* Gráfica principal */}
                    <Box
                        sx={{
                            position: 'relative',
                            mt: 1.5,
                            height: compact ? 190 : 215,
                            borderRadius: 2.5,
                            bgcolor: alpha('#020617', 0.18),
                            border: `1px solid ${alpha('#94a3b8', 0.08)}`,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Retícula interna */}
                        <Box
                            aria-hidden="true"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                opacity: 0.32,
                                backgroundImage: `
                                    linear-gradient(rgba(148,163,184,0.07) 1px, transparent 1px),
                                    linear-gradient(90deg, rgba(148,163,184,0.07) 1px, transparent 1px)
                                `,
                                backgroundSize: '48px 48px',
                            }}
                        />

                        {/* Separación histórico / previsión */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 34,
                                bottom: 20,
                                left: '43%',
                                width: '1px',
                                bgcolor: alpha('#fcd34d', 0.22),
                                zIndex: 2,
                            }}
                        />

                        <Typography
                            sx={{
                                position: 'absolute',
                                left: 14,
                                top: 11,
                                color: '#64748b',
                                fontSize: '0.56rem',
                                fontWeight: 800,
                                letterSpacing: 1,
                                zIndex: 2,
                            }}
                        >
                            HISTÓRICO
                        </Typography>

                        <Typography
                            sx={{
                                position: 'absolute',
                                left: '46%',
                                top: 11,
                                color: '#38bdf8',
                                fontSize: '0.56rem',
                                fontWeight: 800,
                                letterSpacing: 1,
                                zIndex: 2,
                            }}
                        >
                            PREVISIÓN
                        </Typography>

                        <Box
                            sx={{
                                position: 'absolute',
                                left: '43%',
                                right: 0,
                                top: 34,
                                bottom: 20,
                                bgcolor: alpha('#38bdf8', 0.018),
                                zIndex: 0,
                            }}
                        />

                        <Box
                            component="svg"
                            viewBox="0 0 640 220"
                            preserveAspectRatio="none"
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: 1,
                            }}
                        >
                            <defs>
                                <linearGradient
                                    id={`productForecastArea-${compact ? 'compact' : 'desktop'}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="0%"
                                        stopColor="#38bdf8"
                                        stopOpacity="0.15"
                                    />
                                    <stop
                                        offset="100%"
                                        stopColor="#38bdf8"
                                        stopOpacity="0"
                                    />
                                </linearGradient>
                            </defs>

                            <path
                                d="M0 162 C58 151 92 116 142 125 C194 135 231 154 275 128 C317 105 353 112 397 94 C447 73 484 67 526 83 C567 99 597 91 640 65 L640 220 L0 220 Z"
                                fill={`url(#productForecastArea-${compact ? 'compact' : 'desktop'})`}
                            />

                            <motion.path
                                d="M0 162 C58 151 92 116 142 125 C194 135 231 154 275 128 C317 105 353 112 397 94 C447 73 484 67 526 83 C567 99 597 91 640 65"
                                fill="none"
                                stroke="#38bdf8"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                initial={{
                                    pathLength: 0,
                                    opacity: 0,
                                }}
                                animate={{
                                    pathLength: 1,
                                    opacity: 1,
                                }}
                                transition={{
                                    duration: 0.9,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                            />

                            {[
                                [92, 116],
                                [231, 154],
                                [397, 94],
                                [526, 83],
                                [640, 65],
                            ].map(([cx, cy], index) => (
                                <motion.circle
                                    key={`${cx}-${cy}`}
                                    cx={cx}
                                    cy={cy}
                                    r={index >= 2 ? 4 : 3}
                                    fill={
                                        index >= 2
                                            ? '#fcd34d'
                                            : '#38bdf8'
                                    }
                                    initial={{
                                        opacity: 0,
                                        scale: 0,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        scale: 1,
                                    }}
                                    transition={{
                                        delay: 0.42 + index * 0.07,
                                    }}
                                />
                            ))}
                        </Box>

                        {/* Resumen contextual */}
                        <Box
                            sx={{
                                position: 'absolute',
                                right: 12,
                                bottom: 12,
                                zIndex: 3,
                                display: {
                                    xs: 'none',
                                    sm: 'block',
                                },
                                width: 142,
                                p: 1.15,
                                borderRadius: 2,
                                bgcolor: alpha('#081120', 0.78),
                                border: `1px solid ${alpha('#38bdf8', 0.14)}`,
                                backdropFilter: 'blur(10px)',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.51rem',
                                    fontWeight: 800,
                                    letterSpacing: 0.8,
                                }}
                            >
                                PRÓXIMO PERIODO
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.35,
                                    color: '#f8fafc',
                                    fontSize: '1rem',
                                    fontWeight: 800,
                                }}
                            >
                                1.120 uds
                            </Typography>

                            <Stack
                                direction="row"
                                spacing={0.65}
                                alignItems="center"
                                sx={{ mt: 0.7 }}
                            >
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        bgcolor: '#38bdf8',
                                    }}
                                />

                                <Typography
                                    sx={{
                                        color: '#38bdf8',
                                        fontSize: '0.56rem',
                                        fontWeight: 800,
                                    }}
                                >
                                    FIABILIDAD ALTA
                                </Typography>
                            </Stack>
                        </Box>
                    </Box>

                    {/* Serie mensual */}
                    <Box
                        sx={{
                            mt: 1.4,
                            borderRadius: 2.5,
                            border: `1px solid ${alpha('#94a3b8', 0.09)}`,
                            bgcolor: alpha('#0f172a', 0.32),
                            position: 'relative',
                            zIndex: 1,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(3, 1fr)',
                                    sm: 'repeat(5, 1fr)',
                                },
                            }}
                        >
                            {series.map((item, index) => (
                                <Box
                                    key={item.month}
                                    sx={{
                                        position: 'relative',
                                        px: 1.2,
                                        py: 1.05,
                                        display: {
                                            xs:
                                                index < 2
                                                    ? 'none'
                                                    : 'block',
                                            sm: 'block',
                                        },
                                        bgcolor: item.highlighted
                                            ? alpha('#fcd34d', 0.045)
                                            : 'transparent',
                                        borderLeft:
                                            index === 0
                                                ? 'none'
                                                : `1px solid ${alpha('#94a3b8', 0.08)}`,
                                    }}
                                >
                                    {item.type === 'forecast' &&
                                        index === 2 && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    height: '2px',
                                                    bgcolor: '#38bdf8',
                                                }}
                                            />
                                        )}

                                    <Typography
                                        sx={{
                                            color:
                                                item.type === 'forecast'
                                                    ? '#38bdf8'
                                                    : '#64748b',
                                            fontSize: '0.53rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.8,
                                        }}
                                    >
                                        {item.month}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.35,
                                            color: item.highlighted
                                                ? '#fcd34d'
                                                : '#e2e8f0',
                                            fontSize: '0.78rem',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {item.value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            );
        }


        if (step === 1) {
            const contextFields = [
                ['CLIENTE', 'C-024'],
                ['PRODUCTO', 'P-0421'],
                ['COSTE BASE', '35,95 €'],
                ['CANTIDAD', '1.200 uds'],
                ['PERFIL', 'EQUILIBRADO'],
            ];

            return (
                <Box
                    sx={{
                        ...canvasBaseSx,
                        minHeight: compact ? 510 : 560,
                        p: {
                            xs: 2,
                            sm: 2.6,
                            lg: 3,
                        },
                    }}
                >
                    <Typography
                        aria-hidden="true"
                        sx={{
                            position: 'absolute',
                            right: -8,
                            top: 56,
                            color: alpha('#fcd34d', 0.035),
                            fontSize: compact ? '4.5rem' : '7rem',
                            fontWeight: 800,
                            letterSpacing: '-0.07em',
                            userSelect: 'none',
                            pointerEvents: 'none',
                        }}
                    >
                        MARGEN
                    </Typography>

                    {/* Cabecera */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    color: '#fcd34d',
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    letterSpacing: 1.5,
                                }}
                            >
                                OPTIMIZACIÓN CLIENTE-PRODUCTO
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.35,
                                    color: '#f8fafc',
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                }}
                            >
                                Preparación de una futura operación
                            </Typography>
                        </Box>

                        <DemoLabel />
                    </Stack>

                    {/* Estado del análisis */}
                    <Box
                        sx={{
                            mt: 2.2,
                            px: 1.4,
                            py: 1.05,
                            borderRadius: 2.4,
                            bgcolor: alpha('#0f172a', 0.42),
                            border: `1px solid ${alpha('#94a3b8', 0.11)}`,
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={0.8}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    bgcolor: '#38bdf8',
                                    boxShadow: `0 0 12px ${alpha('#38bdf8', 0.45)}`,
                                }}
                            />

                            <Typography
                                sx={{
                                    color: '#cbd5e1',
                                    fontSize: '0.62rem',
                                    fontWeight: 800,
                                    letterSpacing: 0.8,
                                }}
                            >
                                CONTEXTO DISPONIBLE
                            </Typography>
                        </Stack>

                        <Typography
                            sx={{
                                color: '#64748b',
                                fontSize: '0.58rem',
                                fontWeight: 700,
                            }}
                        >
                            Histórico comercial · coste · volumen · estrategia
                        </Typography>
                    </Box>

                    {/* Entradas */}
                    <Box
                        sx={{
                            mt: 1.3,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 0.8,
                                color: '#64748b',
                                fontSize: '0.56rem',
                                fontWeight: 800,
                                letterSpacing: 1,
                            }}
                        >
                            CONTEXTO DE ENTRADA
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: 'repeat(2, 1fr)',
                                    sm: 'repeat(5, minmax(0, 1fr))',
                                },
                                gap: 1,
                            }}
                        >
                            {contextFields.map(([label, value], index) => (
                                <motion.div
                                    key={label}
                                    initial={{
                                        opacity: 0,
                                        y: 8,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                    }}
                                    transition={{
                                        duration: 0.32,
                                        delay: 0.04 + index * 0.045,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: '100%',
                                            p: 1.3,
                                            borderRadius: 2.2,
                                            bgcolor:
                                                label === 'PERFIL'
                                                    ? alpha('#fcd34d', 0.04)
                                                    : alpha('#0f172a', 0.5),
                                            border: `1px solid ${
                                                label === 'PERFIL'
                                                    ? alpha('#fcd34d', 0.14)
                                                    : alpha('#94a3b8', 0.1)
                                            }`,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '0.54rem',
                                                fontWeight: 800,
                                                letterSpacing: 0.85,
                                            }}
                                        >
                                            {label}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.5,
                                                color:
                                                    label === 'PERFIL'
                                                        ? '#fcd34d'
                                                        : '#e2e8f0',
                                                fontSize: '0.75rem',
                                                fontWeight: 800,
                                            }}
                                        >
                                            {value}
                                        </Typography>
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>
                    </Box>

                    {/* Resultado principal */}
                    <Box
                        sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: `1px solid ${alpha('#94a3b8', 0.1)}`,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                mb: 0.8,
                                color: '#64748b',
                                fontSize: '0.56rem',
                                fontWeight: 800,
                                letterSpacing: 1,
                            }}
                        >
                            RESULTADO DEL ANÁLISIS
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                },
                                gap: 1.2,
                            }}
                        >
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 14,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{
                                    duration: 0.38,
                                    delay: 0.12,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        p: 1.8,
                                        borderRadius: 2.7,
                                        bgcolor: alpha('#fcd34d', 0.055),
                                        border: `1px solid ${alpha('#fcd34d', 0.2)}`,
                                        position: 'relative',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: -50,
                                            right: -30,
                                            width: 130,
                                            height: 130,
                                            borderRadius: '50%',
                                            background: `radial-gradient(
                                                circle,
                                                ${alpha('#fcd34d', 0.12)} 0%,
                                                transparent 70%
                                            )`,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            position: 'relative',
                                            color: '#94a3b8',
                                            fontSize: '0.58rem',
                                            fontWeight: 800,
                                            letterSpacing: 1,
                                        }}
                                    >
                                        MARGEN RECOMENDADO
                                    </Typography>

                                    <Typography
                                        sx={{
                                            position: 'relative',
                                            mt: 0.8,
                                            color: '#fcd34d',
                                            fontSize: compact
                                                ? '2.3rem'
                                                : '2.75rem',
                                            lineHeight: 1,
                                            fontWeight: 800,
                                            letterSpacing: '-0.055em',
                                        }}
                                    >
                                        18,5 %
                                    </Typography>

                                    <Typography
                                        sx={{
                                            position: 'relative',
                                            mt: 0.7,
                                            color: '#64748b',
                                            fontSize: '0.66rem',
                                        }}
                                    >
                                        Margen aplicado sobre coste.
                                    </Typography>
                                </Box>
                            </motion.div>

                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 14,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{
                                    duration: 0.38,
                                    delay: 0.18,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        p: 1.8,
                                        borderRadius: 2.7,
                                        bgcolor: alpha('#38bdf8', 0.035),
                                        border: `1px solid ${alpha('#38bdf8', 0.14)}`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#94a3b8',
                                            fontSize: '0.58rem',
                                            fontWeight: 800,
                                            letterSpacing: 1,
                                        }}
                                    >
                                        PRECIO DERIVADO
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.8,
                                            color: '#f8fafc',
                                            fontSize: compact
                                                ? '2.3rem'
                                                : '2.75rem',
                                            lineHeight: 1,
                                            fontWeight: 800,
                                            letterSpacing: '-0.055em',
                                        }}
                                    >
                                        42,60 €
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.7,
                                            color: '#64748b',
                                            fontSize: '0.66rem',
                                        }}
                                    >
                                        Derivado del coste y el margen recomendado.
                                    </Typography>
                                </Box>
                            </motion.div>
                        </Box>
                    </Box>

                    {/* Interpretación */}
                    <Box
                        sx={{
                            mt: 1.2,
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1.4fr 0.6fr',
                            },
                            gap: 1.1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                px: 1.4,
                                py: 1.15,
                                borderRadius: 2.4,
                                bgcolor: alpha('#0f172a', 0.48),
                                border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.53rem',
                                    fontWeight: 800,
                                    letterSpacing: 0.9,
                                }}
                            >
                                LECTURA
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.45,
                                    color: '#cbd5e1',
                                    fontSize: '0.68rem',
                                    lineHeight: 1.45,
                                }}
                            >
                                La recomendación se interpreta junto al volumen,
                                el coste base y el perfil estratégico seleccionado.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 0.8,
                            }}
                        >
                            {[
                                ['CONFIANZA', 'ALTA', '#38bdf8'],
                                ['ADVERTENCIAS', '1', '#fcd34d'],
                            ].map(([label, value, color]) => (
                                <Box
                                    key={label}
                                    sx={{
                                        px: 1,
                                        py: 1.05,
                                        borderRadius: 2.2,
                                        bgcolor: alpha(String(color), 0.035),
                                        border: `1px solid ${alpha(String(color), 0.1)}`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.5rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.7,
                                        }}
                                    >
                                        {label}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.35,
                                            color,
                                            fontSize: '0.72rem',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Control humano */}
                    <Stack
                        direction="row"
                        spacing={0.8}
                        alignItems="center"
                        sx={{
                            mt: 1.1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: '#fcd34d',
                            }}
                        />

                        <Typography
                            sx={{
                                color: '#64748b',
                                fontSize: '0.57rem',
                                fontWeight: 700,
                                letterSpacing: 0.25,
                            }}
                        >
                            Recomendación preparada para revisión · decisión final
                            del usuario.
                        </Typography>
                    </Stack>
                </Box>
            );
        }


        if (step === 2) {
            const scenarios = [
                {
                    label: 'PUNTUAL',
                    quantity: '500 uds',
                    margin: '20,2 %',
                    price: '43,90 €',
                    note: 'Mayor margen unitario',
                },
                {
                    label: 'RECURRENTE',
                    quantity: '1.000 uds',
                    margin: '18,5 %',
                    price: '42,60 €',
                    note: 'Escenario de referencia',
                    selected: true,
                },
                {
                    label: 'VOLUMEN',
                    quantity: '2.000 uds',
                    margin: '16,8 %',
                    price: '41,95 €',
                    note: 'Mayor volumen',
                },
                {
                    label: 'GRAN ESCALA',
                    quantity: '5.000 uds',
                    margin: '15,4 %',
                    price: '41,30 €',
                    note: 'Condición de escala',
                },
            ];

            return (
                <Box
                    sx={{
                        ...canvasBaseSx,
                        minHeight: compact ? 510 : 560,
                        p: {
                            xs: 2,
                            sm: 2.6,
                            lg: 3,
                        },
                    }}
                >
                    <Typography
                        aria-hidden="true"
                        sx={{
                            position: 'absolute',
                            right: -12,
                            top: 62,
                            color: alpha('#fcd34d', 0.03),
                            fontSize: compact ? '3.4rem' : '5.8rem',
                            fontWeight: 800,
                            letterSpacing: '-0.07em',
                            userSelect: 'none',
                            pointerEvents: 'none',
                        }}
                    >
                        ESCENARIOS
                    </Typography>

                    {/* Cabecera */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={1}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    color: '#fcd34d',
                                    fontSize: '0.65rem',
                                    fontWeight: 800,
                                    letterSpacing: 1.5,
                                }}
                            >
                                MATRIZ DE NEGOCIACIÓN
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.35,
                                    color: '#f8fafc',
                                    fontSize: '0.9rem',
                                    fontWeight: 700,
                                }}
                            >
                                Comparación de condiciones por volumen
                            </Typography>
                        </Box>

                        <DemoLabel />
                    </Stack>

                    {/* Contexto de la operación */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, 1fr)',
                                sm: 'repeat(4, 1fr)',
                            },
                            gap: 1,
                            mt: 2.2,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        {[
                            ['CLIENTE', 'C-024'],
                            ['PRODUCTO', 'P-0421'],
                            ['COSTE BASE', '35,95 €'],
                            ['PERFIL', 'EQUILIBRADO'],
                        ].map(([label, value], index) => (
                            <motion.div
                                key={label}
                                initial={{
                                    opacity: 0,
                                    y: 8,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{
                                    duration: 0.32,
                                    delay: 0.04 + index * 0.05,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        px: 1.3,
                                        py: 1.05,
                                        borderRadius: 2.2,
                                        bgcolor:
                                            label === 'PERFIL'
                                                ? alpha('#fcd34d', 0.04)
                                                : alpha('#0f172a', 0.48),
                                        border: `1px solid ${
                                            label === 'PERFIL'
                                                ? alpha('#fcd34d', 0.14)
                                                : alpha('#94a3b8', 0.1)
                                        }`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.53rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.85,
                                        }}
                                    >
                                        {label}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.4,
                                            color:
                                                label === 'PERFIL'
                                                    ? '#fcd34d'
                                                    : '#e2e8f0',
                                            fontSize: '0.73rem',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            </motion.div>
                        ))}
                    </Box>

                    {/* Matriz */}
                    <Box
                        sx={{
                            mt: 1.5,
                            position: 'relative',
                            zIndex: 1,
                            overflow: 'hidden',
                            borderRadius: 2.7,
                            border: `1px solid ${alpha('#94a3b8', 0.11)}`,
                            bgcolor: alpha('#0f172a', 0.38),
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1.15fr 0.85fr 0.85fr',
                                    sm: '1.25fr 0.9fr 0.9fr 0.9fr 1.3fr',
                                },
                                px: 1.6,
                                py: 1.05,
                                bgcolor: alpha('#020617', 0.28),
                                borderBottom: `1px solid ${alpha('#94a3b8', 0.1)}`,
                            }}
                        >
                            {[
                                'ESCENARIO',
                                'CANTIDAD',
                                'MARGEN',
                                'PRECIO',
                                'LECTURA',
                            ].map((label, index) => (
                                <Typography
                                    key={label}
                                    sx={{
                                        display: {
                                            xs:
                                                index === 3 || index === 4
                                                    ? 'none'
                                                    : 'block',
                                            sm: 'block',
                                        },
                                        color: '#64748b',
                                        fontSize: '0.54rem',
                                        fontWeight: 800,
                                        letterSpacing: 0.8,
                                        pl: index === 4 ? 1.5 : 0,
                                        textAlign:
                                            index >= 1 && index <= 3
                                                ? 'right'
                                                : 'left',
                                    }}
                                >
                                    {label}
                                </Typography>
                            ))}
                        </Box>

                        {scenarios.map((scenario, index) => (
                            <motion.div
                                key={scenario.label}
                                initial={{
                                    opacity: 0,
                                    x: 16,
                                }}
                                animate={{
                                    opacity: 1,
                                    x: 0,
                                }}
                                transition={{
                                    duration: 0.34,
                                    delay: 0.08 + index * 0.07,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1.15fr 0.85fr 0.85fr',
                                            sm: '1.25fr 0.9fr 0.9fr 0.9fr 1.3fr',
                                        },
                                        alignItems: 'center',
                                        px: 1.6,
                                        py: compact ? 1.25 : 1.4,
                                        bgcolor: scenario.selected
                                            ? alpha('#fcd34d', 0.055)
                                            : 'transparent',
                                        borderBottom:
                                            index === scenarios.length - 1
                                                ? 'none'
                                                : `1px solid ${alpha('#94a3b8', 0.075)}`,
                                    }}
                                >
                                    {scenario.selected && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                bottom: 0,
                                                width: '2px',
                                                bgcolor: '#fcd34d',
                                                boxShadow: `0 0 12px ${alpha('#fcd34d', 0.4)}`,
                                            }}
                                        />
                                    )}

                                    <Stack
                                        direction="row"
                                        spacing={0.7}
                                        alignItems="center"
                                    >
                                        <Box
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                bgcolor: scenario.selected
                                                    ? '#fcd34d'
                                                    : '#475569',
                                                flexShrink: 0,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                color: scenario.selected
                                                    ? '#fcd34d'
                                                    : '#cbd5e1',
                                                fontSize: '0.65rem',
                                                fontWeight: 800,
                                                letterSpacing: 0.4,
                                            }}
                                        >
                                            {scenario.label}
                                        </Typography>
                                    </Stack>

                                    <Typography
                                        sx={{
                                            color: '#e2e8f0',
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            textAlign: 'right',
                                        }}
                                    >
                                        {scenario.quantity}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            color: scenario.selected
                                                ? '#fcd34d'
                                                : '#e2e8f0',
                                            fontSize: '0.72rem',
                                            fontWeight: 800,
                                            textAlign: 'right',
                                        }}
                                    >
                                        {scenario.margin}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            display: {
                                                xs: 'none',
                                                sm: 'block',
                                            },
                                            color: '#f8fafc',
                                            fontSize: '0.72rem',
                                            fontWeight: 700,
                                            textAlign: 'right',
                                        }}
                                    >
                                        {scenario.price}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            display: {
                                                xs: 'none',
                                                sm: 'block',
                                            },
                                            pl: 1.5,
                                            color: scenario.selected
                                                ? '#cbd5e1'
                                                : '#64748b',
                                            fontSize: '0.58rem',
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {scenario.note}
                                    </Typography>
                                </Box>
                            </motion.div>
                        ))}
                    </Box>

                    {/* Escenario seleccionado */}
                    <Box
                        sx={{
                            mt: 1.35,
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: '1.25fr 0.75fr',
                            },
                            gap: 1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                px: 1.4,
                                py: 1.15,
                                borderRadius: 2.4,
                                bgcolor: alpha('#fcd34d', 0.035),
                                border: `1px solid ${alpha('#fcd34d', 0.11)}`,
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '0.52rem',
                                    fontWeight: 800,
                                    letterSpacing: 0.85,
                                }}
                            >
                                ESCENARIO ACTIVO
                            </Typography>

                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1}
                                justifyContent="space-between"
                                sx={{ mt: 0.55 }}
                            >
                                <Typography
                                    sx={{
                                        color: '#f8fafc',
                                        fontSize: '0.72rem',
                                        fontWeight: 800,
                                    }}
                                >
                                    Recurrente · 1.000 uds
                                </Typography>

                                <Typography
                                    sx={{
                                        color: '#fcd34d',
                                        fontSize: '0.72rem',
                                        fontWeight: 800,
                                    }}
                                >
                                    18,5 % · 42,60 €
                                </Typography>
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 0.8,
                            }}
                        >
                            {[
                                ['MARGEN', '18,5 %'],
                                ['PRECIO', '42,60 €'],
                            ].map(([label, value]) => (
                                <Box
                                    key={label}
                                    sx={{
                                        px: 1,
                                        py: 1.05,
                                        borderRadius: 2.2,
                                        bgcolor: alpha('#0f172a', 0.48),
                                        border: `1px solid ${alpha('#94a3b8', 0.1)}`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.49rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.7,
                                        }}
                                    >
                                        {label}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.3,
                                            color:
                                                label === 'MARGEN'
                                                    ? '#fcd34d'
                                                    : '#f8fafc',
                                            fontSize: '0.7rem',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* Lectura final */}
                    <Stack
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                        sx={{
                            mt: 1.1,
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: '#38bdf8',
                            }}
                        />

                        <Typography
                            sx={{
                                color: '#64748b',
                                fontSize: '0.56rem',
                                fontWeight: 700,
                            }}
                        >
                            El volumen modifica las condiciones comparadas antes
                            de llevar la propuesta a negociación.
                        </Typography>
                    </Stack>
                </Box>
            );
        }


        const optimizationRows = [
            {
                client: 'C-024',
                product: 'P-421',
                margin: '18,5 %',
                price: '42,60 €',
                status: 'COMPLETADA',
            },
            {
                client: 'C-018',
                product: 'P-093',
                margin: '21,2 %',
                price: '31,40 €',
                status: 'COMPLETADA',
            },
            {
                client: 'C-112',
                product: 'P-204',
                margin: '16,9 %',
                price: '58,20 €',
                status: 'REVISAR',
                selected: true,
            },
            {
                client: 'C-084',
                product: 'P-118',
                margin: '19,1 %',
                price: '37,80 €',
                status: 'COMPLETADA',
            },
            {
                client: 'C-057',
                product: 'P-307',
                margin: '17,4 %',
                price: '46,10 €',
                status: 'COMPLETADA',
            },
        ];

        return (
            <Box
                sx={{
                    ...canvasBaseSx,
                    minHeight: compact ? 530 : 560,
                    p: {
                        xs: 2,
                        sm: 2.6,
                        lg: 3,
                    },
                }}
            >
                <Typography
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        right: -8,
                        top: 62,
                        color: alpha('#38bdf8', 0.03),
                        fontSize: compact ? '4rem' : '6.5rem',
                        fontWeight: 800,
                        letterSpacing: '-0.07em',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                >
                    CONTROL
                </Typography>

                {/* Cabecera */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                color: '#38bdf8',
                                fontSize: '0.65rem',
                                fontWeight: 800,
                                letterSpacing: 1.5,
                            }}
                        >
                            OPTIMIZACIONES
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.35,
                                color: '#f8fafc',
                                fontSize: '0.9rem',
                                fontWeight: 700,
                            }}
                        >
                            Trabajo por lotes, historial y revisión
                        </Typography>
                    </Box>

                    <DemoLabel />
                </Stack>

                {/* Resumen de lote */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(4, 1fr)',
                        },
                        gap: 1,
                        mt: 2.2,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    {[
                        ['RELACIONES', '5'],
                        ['COMPLETADAS', '4'],
                        ['REVISAR', '1'],
                        ['PERFIL', 'Equilibrado'],
                    ].map(([label, value], index) => {
                        const accent =
                            label === 'REVISAR'
                                ? '#fcd34d'
                                : label === 'COMPLETADAS'
                                  ? '#38bdf8'
                                  : '#94a3b8';

                        return (
                            <motion.div
                                key={label}
                                initial={{
                                    opacity: 0,
                                    y: 8,
                                }}
                                animate={{
                                    opacity: 1,
                                    y: 0,
                                }}
                                transition={{
                                    duration: 0.32,
                                    delay: 0.04 + index * 0.05,
                                }}
                            >
                                <Box
                                    sx={{
                                        height: '100%',
                                        px: 1.25,
                                        py: 1,
                                        borderRadius: 2.2,
                                        bgcolor: alpha(accent, 0.035),
                                        border: `1px solid ${alpha(accent, 0.1)}`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '0.51rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.8,
                                        }}
                                    >
                                        {label}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 0.38,
                                            color:
                                                label === 'REVISAR'
                                                    ? '#fcd34d'
                                                    : label === 'COMPLETADAS'
                                                      ? '#38bdf8'
                                                      : '#e2e8f0',
                                            fontSize: '0.74rem',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                </Box>
                            </motion.div>
                        );
                    })}
                </Box>

                {/* Navegación visual */}
                <Stack
                    direction="row"
                    spacing={0.7}
                    sx={{
                        mt: 1.35,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Box
                        sx={{
                            px: 1.15,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: alpha('#38bdf8', 0.055),
                            border: `1px solid ${alpha('#38bdf8', 0.15)}`,
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#38bdf8',
                                fontSize: '0.54rem',
                                fontWeight: 800,
                                letterSpacing: 0.75,
                            }}
                        >
                            LOTE ACTUAL
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            px: 1.15,
                            py: 0.5,
                            borderRadius: 999,
                            bgcolor: alpha('#94a3b8', 0.025),
                            border: `1px solid ${alpha('#94a3b8', 0.09)}`,
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#64748b',
                                fontSize: '0.54rem',
                                fontWeight: 800,
                                letterSpacing: 0.75,
                            }}
                        >
                            HISTORIAL
                        </Typography>
                    </Box>
                </Stack>

                {/* Tabla */}
                <Box
                    sx={{
                        mt: 1.15,
                        position: 'relative',
                        zIndex: 1,
                        overflow: 'hidden',
                        borderRadius: 2.7,
                        border: `1px solid ${alpha('#94a3b8', 0.11)}`,
                        bgcolor: alpha('#0f172a', 0.4),
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr 1fr 0.9fr',
                                sm: '0.8fr 0.8fr 0.8fr 0.9fr 1fr',
                            },
                            px: 1.5,
                            py: 1,
                            bgcolor: alpha('#020617', 0.28),
                            borderBottom: `1px solid ${alpha('#94a3b8', 0.1)}`,
                        }}
                    >
                        {[
                            'CLIENTE',
                            'PRODUCTO',
                            'MARGEN',
                            'PRECIO',
                            'ESTADO',
                        ].map((label, index) => (
                            <Typography
                                key={label}
                                sx={{
                                    display: {
                                        xs:
                                            index === 3 || index === 4
                                                ? 'none'
                                                : 'block',
                                        sm: 'block',
                                    },
                                    color: '#64748b',
                                    fontSize: '0.53rem',
                                    fontWeight: 800,
                                    letterSpacing: 0.8,
                                    textAlign:
                                        index >= 2 ? 'right' : 'left',
                                }}
                            >
                                {label}
                            </Typography>
                        ))}
                    </Box>

                    {optimizationRows.map((row, rowIndex) => (
                        <motion.div
                            key={`${row.client}-${row.product}`}
                            initial={{
                                opacity: 0,
                                y: 7,
                            }}
                            animate={{
                                opacity: 1,
                                y: 0,
                            }}
                            transition={{
                                duration: 0.31,
                                delay: 0.06 + rowIndex * 0.055,
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr 1fr 0.9fr',
                                        sm: '0.8fr 0.8fr 0.8fr 0.9fr 1fr',
                                    },
                                    alignItems: 'center',
                                    px: 1.5,
                                    py: 1.08,
                                    bgcolor: row.selected
                                        ? alpha('#fcd34d', 0.04)
                                        : 'transparent',
                                    borderBottom:
                                        rowIndex ===
                                        optimizationRows.length - 1
                                            ? 'none'
                                            : `1px solid ${alpha('#94a3b8', 0.07)}`,
                                }}
                            >
                                {row.selected && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: '2px',
                                            bgcolor: '#fcd34d',
                                        }}
                                    />
                                )}

                                <Typography
                                    sx={{
                                        color: row.selected
                                            ? '#f8fafc'
                                            : '#cbd5e1',
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {row.client}
                                </Typography>

                                <Typography
                                    sx={{
                                        color: '#cbd5e1',
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                    }}
                                >
                                    {row.product}
                                </Typography>

                                <Typography
                                    sx={{
                                        color: row.selected
                                            ? '#fcd34d'
                                            : '#e2e8f0',
                                        fontSize: '0.68rem',
                                        fontWeight: 800,
                                        textAlign: 'right',
                                    }}
                                >
                                    {row.margin}
                                </Typography>

                                <Typography
                                    sx={{
                                        display: {
                                            xs: 'none',
                                            sm: 'block',
                                        },
                                        color: '#f8fafc',
                                        fontSize: '0.68rem',
                                        fontWeight: 700,
                                        textAlign: 'right',
                                    }}
                                >
                                    {row.price}
                                </Typography>

                                <Stack
                                    direction="row"
                                    spacing={0.6}
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    sx={{
                                        display: {
                                            xs: 'none',
                                            sm: 'flex',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor:
                                                row.status === 'REVISAR'
                                                    ? '#fcd34d'
                                                    : '#38bdf8',
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            color:
                                                row.status === 'REVISAR'
                                                    ? '#fcd34d'
                                                    : '#38bdf8',
                                            fontSize: '0.53rem',
                                            fontWeight: 800,
                                            letterSpacing: 0.4,
                                        }}
                                    >
                                        {row.status}
                                    </Typography>
                                </Stack>
                            </Box>
                        </motion.div>
                    ))}
                </Box>

                {/* Detalle de la selección */}
                <Box
                    sx={{
                        mt: 1.25,
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: '1.15fr 0.85fr',
                        },
                        gap: 1,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Box
                        sx={{
                            px: 1.4,
                            py: 1.2,
                            borderRadius: 2.5,
                            bgcolor: alpha('#fcd34d', 0.03),
                            border: `1px solid ${alpha('#fcd34d', 0.11)}`,
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            spacing={1}
                        >
                            <Box>
                                <Typography
                                    sx={{
                                        color: '#64748b',
                                        fontSize: '0.51rem',
                                        fontWeight: 800,
                                        letterSpacing: 0.85,
                                    }}
                                >
                                    OPERACIÓN SELECCIONADA
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.35,
                                        color: '#f8fafc',
                                        fontSize: '0.75rem',
                                        fontWeight: 800,
                                    }}
                                >
                                    C-112 · P-204
                                </Typography>
                            </Box>

                            <Typography
                                sx={{
                                    color: '#fcd34d',
                                    fontSize: '0.55rem',
                                    fontWeight: 800,
                                    letterSpacing: 0.55,
                                }}
                            >
                                REVISAR
                            </Typography>
                        </Stack>

                        <Typography
                            sx={{
                                mt: 0.8,
                                color: '#94a3b8',
                                fontSize: '0.62rem',
                                lineHeight: 1.45,
                            }}
                        >
                            La operación conserva su contexto y una advertencia
                            para revisión antes de tomar la decisión final.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 0.75,
                        }}
                    >
                        {[
                            ['PERFIL', 'Equilibrado', '#cbd5e1'],
                            ['CONFIANZA', 'Alta', '#38bdf8'],
                            ['ADVERTENCIAS', '1', '#fcd34d'],
                            ['EXPLICACIÓN', 'Disponible', '#cbd5e1'],
                        ].map(([label, value, color]) => (
                            <Box
                                key={label}
                                sx={{
                                    px: 0.95,
                                    py: 0.9,
                                    borderRadius: 2.1,
                                    bgcolor: alpha(String(color), 0.025),
                                    border: `1px solid ${alpha(String(color), 0.08)}`,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: '#64748b',
                                        fontSize: '0.48rem',
                                        fontWeight: 800,
                                        letterSpacing: 0.65,
                                    }}
                                >
                                    {label}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.25,
                                        color,
                                        fontSize: '0.65rem',
                                        fontWeight: 800,
                                    }}
                                >
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Cierre */}
                <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{
                        mt: 1,
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#fcd34d',
                        }}
                    />

                    <Typography
                        sx={{
                            color: '#64748b',
                            fontSize: '0.55rem',
                            fontWeight: 700,
                        }}
                    >
                        El sistema conserva el análisis; la decisión final
                        permanece en el usuario.
                    </Typography>
                </Stack>
            </Box>
        );

    };

    const StoryText = ({
        story,
        index,
    }: {
        story: (typeof stories)[number];
        index: number;
    }) => {
        const selected = activeStep === index;

        return (
            <Box
                sx={{
                    position: 'relative',
                    maxWidth: 470,
                    py: {
                        xs: 1,
                        md: 5,
                    },
                }}
            >
                <Typography
                    sx={{
                        color: selected
                            ? '#fcd34d'
                            : alpha('#94a3b8', 0.62),
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: 1.8,
                        transition: 'color 0.25s ease',
                    }}
                >
                    {story.eyebrow}
                </Typography>

                <Typography
                    component="h3"
                    sx={{
                        mt: 1.5,
                        color: selected
                            ? '#f8fafc'
                            : alpha('#f8fafc', 0.62),
                        fontSize: {
                            xs: '1.8rem',
                            lg: '2.25rem',
                        },
                        lineHeight: 1.12,
                        fontWeight: 700,
                        letterSpacing: '-0.035em',
                        transition: 'color 0.25s ease',
                    }}
                >
                    {story.title}
                </Typography>

                <Stack spacing={1.4} sx={{ mt: 2.2 }}>
                    {story.paragraphs.map((paragraph) => (
                        <Typography
                            key={paragraph}
                            sx={{
                                color: selected
                                    ? '#94a3b8'
                                    : alpha('#94a3b8', 0.54),
                                fontSize: {
                                    xs: '0.96rem',
                                    lg: '1rem',
                                },
                                lineHeight: 1.72,
                                transition: 'color 0.25s ease',
                            }}
                        >
                            {paragraph}
                        </Typography>
                    ))}
                </Stack>

                <Box
                    sx={{
                        position: 'absolute',
                        left: -22,
                        top: {
                            xs: 10,
                            md: 48,
                        },
                        bottom: {
                            xs: 'auto',
                            md: 44,
                        },
                        width: '1px',
                        height: {
                            xs: 34,
                            md: 'auto',
                        },
                        bgcolor: selected
                            ? alpha('#fcd34d', 0.7)
                            : alpha('#94a3b8', 0.12),
                        boxShadow: selected
                            ? `0 0 16px ${alpha('#fcd34d', 0.22)}`
                            : 'none',
                        transition: 'all 0.25s ease',
                    }}
                />
            </Box>
        );
    };

    return (
        <Box
            id="product-section"
            sx={{
                position: 'relative',
                py: {
                    xs: 10,
                    md: 16,
                },
                bgcolor: '#081120',
                overflow: 'visible',
            }}
        >
            {/* Retícula general de la sección */}
            <Box
                aria-hidden="true"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.07,
                    pointerEvents: 'none',
                    backgroundImage: `
                        linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)
                    `,
                    backgroundSize: '64px 64px',
                }}
            />

            {/* Halo de Pricing */}
            <Box
                aria-hidden="true"
                sx={{
                    position: 'absolute',
                    top: 80,
                    right: -300,
                    width: 760,
                    height: 760,
                    borderRadius: '50%',
                    background: `radial-gradient(
                        circle,
                        ${alpha('#fcd34d', 0.055)} 0%,
                        transparent 70%
                    )`,
                    filter: 'blur(18px)',
                    pointerEvents: 'none',
                }}
            />

            <Container
                maxWidth="xl"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                {/* Cabecera */}
                <RevealSection>
                    <Box
                        sx={{
                            maxWidth: 820,
                            mb: {
                                xs: 7,
                                md: 10,
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                color: '#fcd34d',
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                letterSpacing: 1.9,
                            }}
                        >
                            PRODUCTO ACTUAL
                        </Typography>

                        <Typography
                            component="h2"
                            sx={{
                                mt: 1.6,
                                color: '#f8fafc',
                                fontSize: {
                                    xs: '2.2rem',
                                    md: '3.2rem',
                                },
                                lineHeight: 1.08,
                                fontWeight: 700,
                                letterSpacing: '-0.045em',
                            }}
                        >
                            Del histórico comercial a una decisión con contexto.
                        </Typography>

                        <Typography
                            sx={{
                                mt: 2.2,
                                maxWidth: 760,
                                color: '#94a3b8',
                                fontSize: {
                                    xs: '1rem',
                                    md: '1.08rem',
                                },
                                lineHeight: 1.72,
                            }}
                        >
                            PROAPricing conecta previsión de demanda y decisiones
                            de margen sobre una misma base comercial, trabajando
                            con el contexto específico de cada relación
                            cliente-producto.
                        </Typography>

                        {/* Recorrido funcional */}
                        <Box
                            sx={{
                                mt: 4,
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: 'repeat(5, 1fr)',
                                },
                                maxWidth: 780,
                            }}
                        >
                            {[
                                'HISTÓRICO',
                                'PREVISIÓN',
                                'OPTIMIZACIÓN',
                                'ESCENARIOS',
                                'DECISIÓN',
                            ].map((label, index, array) => (
                                <Box
                                    key={label}
                                    sx={{
                                        position: 'relative',
                                        pb: {
                                            xs: 1.3,
                                            sm: 0,
                                        },
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Box
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: '50%',
                                                bgcolor:
                                                    index === 0
                                                        ? '#fcd34d'
                                                        : index === 1
                                                          ? '#38bdf8'
                                                          : '#475569',
                                                boxShadow:
                                                    index === 0
                                                        ? `0 0 14px ${alpha('#fcd34d', 0.42)}`
                                                        : 'none',
                                                flexShrink: 0,
                                            }}
                                        />

                                        <Typography
                                            sx={{
                                                color:
                                                    index <= 1
                                                        ? '#cbd5e1'
                                                        : '#64748b',
                                                fontSize: '0.6rem',
                                                fontWeight: 800,
                                                letterSpacing: 0.8,
                                            }}
                                        >
                                            {label}
                                        </Typography>
                                    </Stack>

                                    {index < array.length - 1 && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                display: {
                                                    xs: 'none',
                                                    sm: 'block',
                                                },
                                                top: 3,
                                                left: 92,
                                                right: 14,
                                                height: '1px',
                                                bgcolor: alpha('#94a3b8', 0.14),
                                            }}
                                        />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </RevealSection>

                {isDesktop ? (
                    /* =============================================================
                       DESKTOP — NARRATIVA + CANVAS STICKY
                       ============================================================= */
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                'minmax(330px, 0.78fr) minmax(540px, 1.22fr)',
                            gap: {
                                md: 6,
                                lg: 9,
                            },
                            alignItems: 'start',
                        }}
                    >
                        {/* Narrativa */}
                        <Box>
                            {stories.map((story, index) => (
                                <motion.div
                                    key={story.eyebrow}
                                    onViewportEnter={() => setActiveStep(index)}
                                    viewport={{
                                        once: false,
                                        amount: 0.46,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minHeight: '68vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            pl: 3,
                                        }}
                                    >
                                        <StoryText
                                            story={story}
                                            index={index}
                                        />
                                    </Box>
                                </motion.div>
                            ))}
                        </Box>

                        {/* Canvas persistente */}
                        <Box
                            sx={{
                                position: 'relative',
                                alignSelf: 'stretch',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'sticky',
                                    top: 'clamp(120px, 18vh, 180px)',
                                }}
                            >
                                <motion.div
                                    key={activeStep}
                                    initial={{
                                        opacity: 0,
                                        y: 12,
                                        scale: 0.992,
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0,
                                        scale: 1,
                                    }}
                                    transition={{
                                        duration: 0.38,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                >
                                    {renderScene(activeStep)}
                                </motion.div>

                                <Stack
                                    direction="row"
                                    spacing={0.8}
                                    justifyContent="center"
                                    sx={{ mt: 2 }}
                                >
                                    {stories.map((story, index) => (
                                        <Box
                                            key={story.eyebrow}
                                            sx={{
                                                width:
                                                    activeStep === index
                                                        ? 28
                                                        : 7,
                                                height: 4,
                                                borderRadius: 999,
                                                bgcolor:
                                                    activeStep === index
                                                        ? '#fcd34d'
                                                        : alpha('#94a3b8', 0.2),
                                                transition:
                                                    'all 0.28s ease',
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    /* =============================================================
                       MÓVIL — CADA TEXTO CON SU ESCENA COMPACTA
                       ============================================================= */
                    <Stack spacing={10}>
                        {stories.map((story, index) => (
                            <motion.div
                                key={story.eyebrow}
                                onViewportEnter={() => setActiveStep(index)}
                                viewport={{
                                    once: false,
                                    amount: 0.35,
                                }}
                            >
                                <Box sx={{ pl: 2.5 }}>
                                    <StoryText
                                        story={story}
                                        index={index}
                                    />
                                </Box>

                                <Box sx={{ mt: 3.5 }}>
                                    {renderScene(index, true)}
                                </Box>
                            </motion.div>
                        ))}
                    </Stack>
                )}
            </Container>
        </Box>
    );
};

const LandingPage: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const location = useLocation(); // <--- 1. Hook de navegación

    // <--- 2. EFECTO DE ATERRIZAJE (SCROLL AUTOMÁTICO)
    useEffect(() => {
        if (location.state && (location.state as any).targetSection) {
            const targetId = (location.state as any).targetSection;
            
            // Reducimos el tiempo a 100ms (suficiente para renderizar, imperceptible al ojo)
            setTimeout(() => {
                const element = document.getElementById(targetId);
                if (element) {
                    // CAMBIO CLAVE: 'auto' en lugar de 'smooth'
                    // Esto fuerza un salto instantáneo. El usuario "aparece" en la sección.
                    element.scrollIntoView({ behavior: 'auto', block: 'start' });
                    
                    // Limpieza del estado
                    window.history.replaceState({}, document.title);
                }
            }, 100); 
        }
    }, [location]);
    
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

    const {
        formState,
        isSubmitting,
        submitStatus,
        handleFormChange,
        handleSubmit,
    } = usePublicLeadForm({
        initialValues: {
            name: '',
            email: '',
            company: '',
            tier: 'Enterprise',
            message: '',
        },
        requiredFields: [
            'name',
            'email',
        ],
        buildPayload: (values) => ({
            name: values.name,
            email: values.email,
            company: values.company,
            tier: values.tier,
            message: values.message,
        }),
    });

    // La lógica de cambios, validación, envío y reinicio
    // se gestiona desde usePublicLeadForm.

    const inputSx = {
        '& .MuiOutlinedInput-root': {
            color: '#f8fafc',
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            '& fieldset': { borderColor: '#334155' },
            '&:hover fieldset': { borderColor: '#94a3b8' },
            '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
        },
        '& .MuiInputLabel-root': { color: '#94a3b8' },
        '& .MuiInputLabel-root.Mui-focused': { color: '#38bdf8' },
        '& .MuiSelect-icon': { color: '#94a3b8' }
    };

    return (
        <Box sx={{ 
            width: '100%', 
            overflowX: 'clip', 
            bgcolor: '#0f172a', 
            color: 'text.primary' 
        }}>

            {/* =================================================================================
               1. HERO SECTION — PROAPRICING / IDENTIDAD DE LÍNEA
               ================================================================================= */}
            <Box
                id="top"
                sx={{
                    position: 'relative',
                    width: '100%',
                    minHeight: {
                        xs: '100svh',
                        md: '100vh',
                    },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: '#081120',
                }}
            >
                {/* Retícula común del sistema visual PROA */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0.1,
                        pointerEvents: 'none',
                        backgroundImage: `
                            linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)
                        `,
                        backgroundSize: '64px 64px',
                    }}
                />

                {/* Halo principal de PROAPricing */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        top: '42%',
                        left: '50%',
                        width: {
                            xs: 520,
                            md: 980,
                        },
                        height: {
                            xs: 520,
                            md: 760,
                        },
                        transform: 'translate(-50%, -50%)',
                        borderRadius: '50%',
                        pointerEvents: 'none',
                        background: `radial-gradient(
                            ellipse at center,
                            ${alpha('#fcd34d', 0.09)} 0%,
                            ${alpha('#fcd34d', 0.025)} 34%,
                            transparent 70%
                        )`,
                        filter: 'blur(18px)',
                    }}
                />

                {/* Halo técnico secundario */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        right: {
                            xs: -220,
                            md: -120,
                        },
                        bottom: {
                            xs: -220,
                            md: -260,
                        },
                        width: {
                            xs: 460,
                            md: 760,
                        },
                        height: {
                            xs: 460,
                            md: 760,
                        },
                        borderRadius: '50%',
                        pointerEvents: 'none',
                        background: `radial-gradient(
                            circle,
                            ${alpha('#38bdf8', 0.055)} 0%,
                            transparent 68%
                        )`,
                        filter: 'blur(14px)',
                    }}
                />

                {/* Nombre monumental de la línea */}
                <Typography
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        top: {
                            xs: '16%',
                            md: '13%',
                        },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 'max-content',
                        maxWidth: 'none',
                        color: alpha('#f8fafc', 0.035),
                        fontSize: 'clamp(5.3rem, 14vw, 14rem)',
                        fontWeight: 800,
                        lineHeight: 0.84,
                        letterSpacing: '-0.075em',
                        whiteSpace: 'nowrap',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                >
                    PROAPRICING
                </Typography>

                {/* Información de producto convertida en textura de fondo */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        right: {
                            md: '-2%',
                            lg: '2%',
                        },
                        bottom: {
                            md: '4%',
                            lg: '6%',
                        },
                        width: {
                            md: 520,
                            lg: 620,
                        },
                        display: {
                            xs: 'none',
                            md: 'block',
                        },
                        opacity: 0.055,
                        filter: 'blur(1.2px)',
                        transform: 'scale(1.08) rotate(-1.5deg)',
                        transformOrigin: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <PricingHeroPreview />
                </Box>

                {/* Señales comerciales secundarias */}
                <Typography
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        left: {
                            md: '5%',
                            lg: '7%',
                        },
                        top: '32%',
                        display: {
                            xs: 'none',
                            md: 'block',
                        },
                        color: alpha('#94a3b8', 0.075),
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: 2,
                        writingMode: 'vertical-rl',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                >
                    CLIENTE · PRODUCTO
                </Typography>

                <Typography
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        right: {
                            md: '7%',
                            lg: '9%',
                        },
                        top: '29%',
                        display: {
                            xs: 'none',
                            md: 'block',
                        },
                        color: alpha('#fcd34d', 0.065),
                        fontSize: {
                            md: '2.6rem',
                            lg: '3.4rem',
                        },
                        fontWeight: 800,
                        letterSpacing: '-0.04em',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                >
                    MARGEN
                </Typography>

                <Typography
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        left: {
                            md: '9%',
                            lg: '12%',
                        },
                        bottom: '16%',
                        display: {
                            xs: 'none',
                            md: 'block',
                        },
                        color: alpha('#38bdf8', 0.055),
                        fontSize: {
                            md: '2.2rem',
                            lg: '3rem',
                        },
                        fontWeight: 800,
                        letterSpacing: '-0.04em',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                >
                    DEMANDA
                </Typography>

                {/* Contenido principal */}
                <Container
                    maxWidth="lg"
                    sx={{
                        position: 'relative',
                        zIndex: 3,
                        width: '100%',
                        py: {
                            xs: 13,
                            md: 15,
                        },
                    }}
                >
                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 26,
                            scale: 0.99,
                        }}
                        animate={{
                            opacity: 1,
                            y: 0,
                            scale: 1,
                        }}
                        transition={{
                            duration: 0.85,
                            ease: [0.22, 1, 0.36, 1],
                        }}
                    >
                        <Stack
                            alignItems="center"
                            sx={{
                                width: '100%',
                                textAlign: 'center',
                            }}
                        >
                            {/* Símbolo de la marca madre */}
                            <Box
                                component="img"
                                src="/logo.png"
                                alt="PROA"
                                sx={{
                                    display: 'block',
                                    width: 'auto',
                                    height: {
                                        xs: 78,
                                        sm: 88,
                                        md: 104,
                                    },
                                    mb: {
                                        xs: 2.4,
                                        md: 2.8,
                                    },
                                    objectFit: 'contain',
                                    filter: `drop-shadow(
                                        0 18px 46px ${alpha('#020617', 0.52)}
                                    )`,
                                }}
                            />

                            {/* Marca madre */}
                            <Typography
                                sx={{
                                    color: '#f8fafc',
                                    fontSize: {
                                        xs: '0.78rem',
                                        md: '0.86rem',
                                    },
                                    fontWeight: 800,
                                    letterSpacing: {
                                        xs: 3.8,
                                        md: 5,
                                    },
                                }}
                            >
                                PROA
                            </Typography>

                            {/* Línea */}
                            <Typography
                                component="h1"
                                sx={{
                                    mt: {
                                        xs: 0.8,
                                        md: 0.5,
                                    },
                                    color: '#fcd34d',
                                    fontSize: {
                                        xs: '4.4rem',
                                        sm: '6rem',
                                        md: '7.8rem',
                                        lg: '9rem',
                                    },
                                    fontWeight: 800,
                                    lineHeight: 0.95,
                                    letterSpacing: '-0.065em',
                                    textShadow: `0 18px 80px ${alpha(
                                        '#fcd34d',
                                        0.12
                                    )}`,
                                }}
                            >
                                PRICING
                            </Typography>

                            {/* Descriptor del territorio */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                spacing={{
                                    xs: 1.2,
                                    sm: 1.8,
                                }}
                                sx={{
                                    mt: {
                                        xs: 3,
                                        md: 3.5,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: {
                                            xs: 26,
                                            sm: 42,
                                        },
                                        height: 1,
                                        bgcolor: alpha('#fcd34d', 0.42),
                                    }}
                                />

                                <Typography
                                    sx={{
                                        color: '#cbd5e1',
                                        fontSize: {
                                            xs: '0.67rem',
                                            sm: '0.76rem',
                                        },
                                        fontWeight: 700,
                                        letterSpacing: {
                                            xs: 1.4,
                                            sm: 2.2,
                                        },
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    PREVISIÓN · MARGEN · PRECIO
                                </Typography>

                                <Box
                                    sx={{
                                        width: {
                                            xs: 26,
                                            sm: 42,
                                        },
                                        height: 1,
                                        bgcolor: alpha('#fcd34d', 0.42),
                                    }}
                                />
                            </Stack>

                            {/* Frase de posicionamiento */}
                            <Typography
                                sx={{
                                    mt: {
                                        xs: 2.5,
                                        md: 2.8,
                                    },
                                    maxWidth: 620,
                                    color: '#94a3b8',
                                    fontSize: {
                                        xs: '1rem',
                                        md: '1.13rem',
                                    },
                                    fontWeight: 400,
                                    lineHeight: 1.65,
                                    letterSpacing: '-0.01em',
                                }}
                            >
                                Datos comerciales convertidos en contexto para decidir.
                            </Typography>

                            {/* Acceso discreto al contenido */}
                            <Button
                                variant="text"
                                endIcon={
                                    <ArrowForwardIcon
                                        sx={{
                                            transform: 'rotate(90deg)',
                                            fontSize: '1rem !important',
                                        }}
                                    />
                                }
                                onClick={() =>
                                    document
                                        .getElementById('pricing-reading')
                                        ?.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'start',
                                        })
                                }
                                sx={{
                                    mt: {
                                        xs: 3.5,
                                        md: 4,
                                    },
                                    px: 0,
                                    color: alpha('#fcd34d', 0.82),
                                    fontWeight: 700,
                                    fontSize: '0.82rem',
                                    textTransform: 'none',
                                    letterSpacing: 0.2,
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        color: '#fcd34d',
                                    },
                                }}
                            >
                                Explorar PROAPricing
                            </Button>
                        </Stack>
                    </motion.div>
                </Container>

                {/* Fusión con la siguiente sección */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        zIndex: 2,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 130,
                        pointerEvents: 'none',
                        background:
                            'linear-gradient(180deg, transparent 0%, #081120 100%)',
                    }}
                />
            </Box>

            {/* =================================================================================
               2. PRODUCT DECISION SURFACE
               ================================================================================= */}
            <PricingDecisionSurface />

            {/* =================================================================================
               3. PRODUCT SECTION — SCROLL NARRATIVO PROAPRICING
               ================================================================================= */}
            <PricingProductStory />

            <Divider sx={{ opacity: 0.1 }} />

            {/* =================================================================================
               4. ORIGIN STORY — PROAPRICING / SISTEMA VISUAL PROA
               ================================================================================= */}
            <Box
                id="origin-section"
                sx={{
                    position: 'relative',
                    py: {
                        xs: 11,
                        md: 16,
                    },
                    bgcolor: '#081120',
                    overflow: 'hidden',
                }}
            >
                {/* Retícula del sistema visual PROA */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        opacity: 0.06,
                        pointerEvents: 'none',
                        backgroundImage: `
                            linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)
                        `,
                        backgroundSize: '64px 64px',
                    }}
                />

                {/* Halo de línea */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        left: {
                            xs: -260,
                            md: -180,
                        },
                        top: '38%',
                        width: {
                            xs: 520,
                            md: 760,
                        },
                        height: {
                            xs: 520,
                            md: 760,
                        },
                        borderRadius: '50%',
                        pointerEvents: 'none',
                        background: `radial-gradient(
                            circle,
                            ${alpha('#fcd34d', 0.05)} 0%,
                            transparent 70%
                        )`,
                        filter: 'blur(18px)',
                    }}
                />

                {/* Palabra monumental de fondo */}
                <Typography
                    aria-hidden="true"
                    sx={{
                        position: 'absolute',
                        right: {
                            xs: '-18%',
                            md: '-4%',
                        },
                        top: {
                            xs: '10%',
                            md: '4%',
                        },
                        color: alpha('#f8fafc', 0.028),
                        fontSize: {
                            xs: '6rem',
                            md: '12rem',
                            lg: '15rem',
                        },
                        fontWeight: 800,
                        lineHeight: 0.85,
                        letterSpacing: '-0.075em',
                        whiteSpace: 'nowrap',
                        userSelect: 'none',
                        pointerEvents: 'none',
                    }}
                >
                    ORIGEN
                </Typography>

                <Container
                    maxWidth="xl"
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Grid
                        container
                        spacing={{
                            xs: 7,
                            md: 10,
                            lg: 14,
                        }}
                        alignItems="center"
                    >
                        {/* Firma del fundador */}
                        <Grid item xs={12} md={4}>
                            <RevealSection>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        maxWidth: {
                                            xs: 520,
                                            md: 330,
                                        },
                                        pl: {
                                            xs: 2.6,
                                            md: 3.2,
                                        },
                                        py: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            bottom: 0,
                                            width: '1px',
                                            bgcolor: alpha('#fcd34d', 0.68),
                                            boxShadow: `0 0 18px ${alpha('#fcd34d', 0.18)}`,
                                        }}
                                    />

                                    <Typography
                                        sx={{
                                            color: '#fcd34d',
                                            fontSize: '0.66rem',
                                            fontWeight: 800,
                                            letterSpacing: 1.8,
                                        }}
                                    >
                                        FUNDADOR
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 1.4,
                                            color: '#f8fafc',
                                            fontSize: {
                                                xs: '2rem',
                                                md: '2.5rem',
                                            },
                                            lineHeight: 1,
                                            fontWeight: 800,
                                            letterSpacing: '-0.045em',
                                        }}
                                    >
                                        Pablo Pazos
                                    </Typography>

                                    <Typography
                                        sx={{
                                            mt: 1,
                                            color: '#94a3b8',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                        }}
                                    >
                                        Fundador de PROA
                                    </Typography>

                                    <Box
                                        sx={{
                                            mt: 4,
                                            pt: 2.2,
                                            borderTop: `1px solid ${alpha('#94a3b8', 0.11)}`,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: '#64748b',
                                                fontSize: '0.56rem',
                                                fontWeight: 800,
                                                letterSpacing: 1.2,
                                            }}
                                        >
                                            UNA MISMA LÓGICA
                                        </Typography>

                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{ mt: 1.2 }}
                                        >
                                            {[
                                                'DATOS',
                                                'CONTEXTO',
                                                'DECISIÓN',
                                            ].map((label, index, array) => (
                                                <React.Fragment key={label}>
                                                    <Typography
                                                        sx={{
                                                            color:
                                                                index === array.length - 1
                                                                    ? '#fcd34d'
                                                                    : '#cbd5e1',
                                                            fontSize: '0.62rem',
                                                            fontWeight: 800,
                                                            letterSpacing: 0.7,
                                                        }}
                                                    >
                                                        {label}
                                                    </Typography>

                                                    {index < array.length - 1 && (
                                                        <Box
                                                            sx={{
                                                                width: 18,
                                                                height: '1px',
                                                                bgcolor: alpha('#94a3b8', 0.25),
                                                            }}
                                                        />
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </Stack>
                                    </Box>
                                </Box>
                            </RevealSection>
                        </Grid>

                        {/* Historia */}
                        <Grid item xs={12} md={8}>
                            <RevealSection delay={0.15}>
                                <Box
                                    sx={{
                                        maxWidth: 790,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: '#fcd34d',
                                            fontSize: '0.7rem',
                                            fontWeight: 800,
                                            letterSpacing: 1.9,
                                        }}
                                    >
                                        EL ORIGEN
                                    </Typography>

                                    <Typography
                                        component="h2"
                                        sx={{
                                            mt: 1.7,
                                            color: '#f8fafc',
                                            fontSize: {
                                                xs: '2.25rem',
                                                md: '3.25rem',
                                                lg: '3.6rem',
                                            },
                                            lineHeight: 1.08,
                                            fontWeight: 700,
                                            letterSpacing: '-0.045em',
                                        }}
                                    >
                                        De trabajar con información industrial a
                                        construir PROAPricing.
                                    </Typography>

                                    <Stack
                                        spacing={2.4}
                                        sx={{
                                            mt: {
                                                xs: 3.5,
                                                md: 4.2,
                                            },
                                            maxWidth: 720,
                                        }}
                                    >
                                        <Typography
                                            lang="es"
                                            sx={{
                                                color: '#94a3b8',
                                                fontSize: {
                                                    xs: '1rem',
                                                    md: '1.08rem',
                                                },
                                                lineHeight: 1.8,
                                                textAlign: {
                                                    xs: 'left',
                                                    md: 'justify',
                                                },
                                                textAlignLast: 'left',
                                                hyphens: 'auto',
                                            }}
                                        >
                                            Mi experiencia en entornos industriales
                                            me acostumbró a trabajar con procesos en
                                            los que disponer de datos no era
                                            suficiente. Había que convertirlos en
                                            contexto para poder tomar una decisión.
                                        </Typography>

                                        <Typography
                                            lang="es"
                                            sx={{
                                                color: '#94a3b8',
                                                fontSize: {
                                                    xs: '1rem',
                                                    md: '1.08rem',
                                                },
                                                lineHeight: 1.8,
                                                textAlign: {
                                                    xs: 'left',
                                                    md: 'justify',
                                                },
                                                textAlignLast: 'left',
                                                hyphens: 'auto',
                                            }}
                                        >
                                            Con el tiempo, esa misma pregunta se
                                            trasladó al ámbito comercial: si una
                                            empresa ya dispone de años de histórico
                                            de ventas, ¿cómo podemos utilizarlo para
                                            anticipar demanda y preparar mejor una
                                            decisión de margen y precio?
                                        </Typography>
                                    </Stack>

                                    {/* Cierre de la historia */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            mt: {
                                                xs: 4,
                                                md: 5,
                                            },
                                            pt: 2.5,
                                            maxWidth: 620,
                                            borderTop: `1px solid ${alpha('#94a3b8', 0.12)}`,
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1.2}
                                            alignItems="center"
                                        >
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    bgcolor: '#fcd34d',
                                                    boxShadow: `0 0 16px ${alpha('#fcd34d', 0.38)}`,
                                                    flexShrink: 0,
                                                }}
                                            />

                                            <Typography
                                                sx={{
                                                    color: '#f8fafc',
                                                    fontSize: {
                                                        xs: '1.1rem',
                                                        md: '1.25rem',
                                                    },
                                                    fontWeight: 700,
                                                    letterSpacing: '-0.015em',
                                                }}
                                            >
                                                PROAPricing empieza ahí.
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Box>
                            </RevealSection>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* =================================================================================
               NUEVA SECCIÓN: AUDITORÍA DE RENTABILIDAD (ROI FOCUS)
               ================================================================================= */}
            <Box id="audit-section" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#0b1120', position: 'relative' }}>
                <Container maxWidth="lg">
                    {/* ENCABEZADO: DIRECTO AL ROI */}
                    <RevealSection>
                        <Box sx={{ textAlign: 'center', mb: 10 }}>
                            <Typography variant="overline" sx={{ color: '#fcd34d', fontWeight: 700, letterSpacing: 2 }}>
                                PROOF OF VALUE
                            </Typography>
                            <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 800, color: '#f8fafc', mt: 1, mb: 2 }}>
                                Auditoría de Rentabilidad Histórica
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#94a3b8', fontSize: '1.2rem', maxWidth: '800px', mx: 'auto', lineHeight: 1.6 }}>
                                Sin coste. Sin riesgo. Descubre exactamente cuánto margen dejaste sobre la mesa en los últimos 12 meses utilizando tus propios datos.
                            </Typography>
                        </Box>
                    </RevealSection>

                    {/* PROCESO DE 3 PASOS (CLEAN BLUEPRINT) */}
                    <Grid container spacing={4} justifyContent="center">
                        
                        {/* PASO 1: INPUT (DATOS) */}
                        <Grid item xs={12} md={4}>
                            <RevealSection delay={0.1}>
                                <Box sx={{ p: 3, height: '100%', borderLeft: '1px solid #334155', transition: 'all 0.3s', '&:hover': { bgcolor: alpha('#fff', 0.02) }, display: 'flex', flexDirection: 'column' }}>
                                    
                                    {/* HEADER */}
                                    <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                                        <CloudUploadIcon sx={{ color: '#38bdf8', fontSize: 32 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f8fafc' }}>1. Ingesta de Datos</Typography>
                                    </Stack>
                                    
                                    {/* BODY TEXT (JUSTIFICADO) */}
                                    <Typography variant="body1" sx={{ color: '#cbd5e1', mb: 2, lineHeight: 1.7, minHeight: '80px', textAlign: 'justify' }}>
                                        Carga tu histórico de ventas en nuestro entorno seguro. Si tus datos requieren limpieza o extracción compleja, solicita la integración técnica gratuita.
                                    </Typography>
                                    
                                    {/* SUBTEXT */}
                                    <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
                                        Puedes subir archivos en formato CSV o Excel estándar.
                                    </Typography>

                                    {/* === NUEVO BLOQUE DE SOPORTE (LLENA EL HUECO) === */}
                                    {/* Usamos mt: 'auto' para empujarlo al fondo si sobra mucho espacio, o margen fijo si queremos pegarlo */}
                                    <Box sx={{ 
                                        mt: 'auto', 
                                        p: 2, 
                                        bgcolor: alpha('#38bdf8', 0.08), 
                                        borderRadius: 2, 
                                        border: `1px dashed ${alpha('#38bdf8', 0.3)}`
                                    }}>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            {/* Asegúrate de tener importado SupportAgentIcon o EngineeringIcon */}
                                            <SupportAgentIcon sx={{ color: '#38bdf8', fontSize: 28 }} />
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 800, lineHeight: 1.2 }}>
                                                    ¿Necesitas ayuda?
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#94a3b8', lineHeight: 1.3, display: 'block', mt: 0.5 }}>
                                                    Nosotros lo hacemos por ti:
                                                    <Box component="span" sx={{ display: 'block', color: '#38bdf8', fontWeight: 600, mt: 0.5 }}>
                                                        soporte@proapricing.ai
                                                    </Box>
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>

                                </Box>
                            </RevealSection>
                        </Grid>

                        {/* PASO 2: PROCESS (MOTOR) */}
                        <Grid item xs={12} md={4}>
                            <RevealSection delay={0.2}>
                                <Box sx={{ p: 3, height: '100%', borderLeft: '1px solid #334155', transition: 'all 0.3s', '&:hover': { bgcolor: alpha('#fff', 0.02) } }}>
                                    <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                                        <MemoryIcon sx={{ color: '#a78bfa', fontSize: 32 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f8fafc' }}>2. Cálculo Intensivo</Typography>
                                    </Stack>
                                    {/* AQUI ESTÁ EL CAMBIO: textAlign: 'justify' */}
                                    <Typography variant="body1" sx={{ color: '#cbd5e1', mb: 2, lineHeight: 1.7, minHeight: '80px', textAlign: 'justify' }}>
                                        El motor entrena modelos de Machine Learning y simula las transacciones de los últimos 12 meses, una a una. Este proceso, de alta carga computacional, se ejecuta durante el ciclo de trabajo nocturno.
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                                        Sin atajos. Análisis inteligente transacción a transacción.
                                    </Typography>
                                </Box>
                            </RevealSection>
                        </Grid>

                        {/* PASO 3: OUTPUT (RESULTADO) */}
                        <Grid item xs={12} md={4}>
                            <RevealSection delay={0.3}>
                                <Box sx={{ p: 3, height: '100%', borderLeft: `1px solid ${alpha('#fcd34d', 0.5)}`, bgcolor: alpha('#fcd34d', 0.02) }}>
                                    <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                                        <QueryStatsIcon sx={{ color: '#fcd34d', fontSize: 32 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#f8fafc' }}>3. Informe de ROI</Typography>
                                    </Stack>
                                    {/* AQUI ESTÁ EL CAMBIO: textAlign: 'justify' */}
                                    <Typography variant="body1" sx={{ color: '#cbd5e1', mb: 2, lineHeight: 1.7, minHeight: '80px', textAlign: 'justify' }}>
                                        A la mañana siguiente recibirás la cifra exacta de dinero que potencialmente ha quedado sobre la mesa (Profit Leakage). Tendrás acceso completo al Dashboard BI y al módulo de optimización con 10 optimizaciones mensuales.
                                    </Typography>
                                    <Stack spacing={1}>
                                        <Chip label="Acceso Business Intelligence" size="small" sx={{ bgcolor: alpha('#fcd34d', 0.1), color: '#fcd34d', width: 'fit-content' }} />
                                        <Chip label="10 Optimizaciones Reales" size="small" sx={{ bgcolor: alpha('#fcd34d', 0.1), color: '#fcd34d', width: 'fit-content' }} />
                                    </Stack>
                                </Box>
                            </RevealSection>
                        </Grid>
                    </Grid>

                    {/* CTA AREA */}
                    <Box sx={{ mt: 8, textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            href="/signup"
                            sx={{
                                bgcolor: '#fcd34d',
                                color: '#0f172a',
                                px: 5,
                                py: 1.5,
                                fontSize: '1rem',
                                fontWeight: 800,
                                '&:hover': { bgcolor: '#fbbf24' }
                            }}
                        >
                            COMENZAR AUDITORÍA AHORA
                        </Button>

                        <Typography
                            variant="body2"
                            sx={{
                                mt: 3,
                                color: '#64748b',
                            }}
                        >
                            ¿Prefieres hablar con nuestro equipo o enviarnos directamente tus datos?
                        </Typography>

                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                            sx={{ mt: 2 }}
                        >
                            <Button
                                variant="outlined"
                                startIcon={<EngineeringIcon />}
                                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                                sx={{
                                    borderColor: alpha('#94a3b8', 0.35),
                                    color: '#cbd5e1',
                                    px: 3,
                                    py: 1.2,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    '&:hover': {
                                        borderColor: '#f8fafc',
                                        color: '#f8fafc',
                                        bgcolor: alpha('#fff', 0.03),
                                    }
                                }}
                            >
                                Contactar con el equipo
                            </Button>

                            <Button
                                variant="text"
                                href="/subir-archivos"
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    color: '#38bdf8',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    borderBottom: `1px solid ${alpha('#38bdf8', 0.45)}`,
                                    borderRadius: 0,
                                    px: 0.5,
                                    '&:hover': {
                                        color: '#7dd3fc',
                                        borderColor: '#7dd3fc',
                                        bgcolor: 'transparent',
                                    }
                                }}
                            >
                                Subir datos para auditoría
                            </Button>
                        </Stack>
                    </Box>
                </Container>
            </Box>

            {/* =================================================================================
               5. PRICING SECTION (REDISEÑO DARK PREMIUM: STRATEGIC TIERS)
               ================================================================================= */}
            <Box id="pricing-section" sx={{ py: { xs: 10, md: 16 }, position: 'relative' }}>
                {/* Glow ambiental de fondo */}
                <Box sx={{ 
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                    width: '120%', height: '80%', 
                    background: 'radial-gradient(circle at center, rgba(56, 189, 248, 0.08) 0%, transparent 60%)', 
                    zIndex: 0, pointerEvents: 'none' 
                }} />

                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <RevealSection>
                        <Box sx={{ textAlign: 'center', mb: 10 }}>
                            <Typography variant="overline" sx={{ color: '#38bdf8', fontWeight: 700, letterSpacing: 2 }}>
                                ESCALABILIDAD
                            </Typography>
                            <Typography variant="h3" sx={{ fontWeight: 800, mt: 1, mb: 2, color: '#f8fafc' }}>
                                Planes Estratégicos
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#94a3b8', fontSize: '1.1rem', maxWidth: '600px', mx: 'auto' }}>
                                Desde la auditoría inicial hasta la autonomía total. Elige la potencia que tu organización requiere hoy.
                            </Typography>
                        </Box>
                    </RevealSection>

                    {/* --- TOGGLE SWITCH MINIMALISTA --- */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ bgcolor: 'rgba(255,255,255,0.03)', p: 1, borderRadius: 10, border: '1px solid #334155' }}>
                            <Typography variant="body2" sx={{ color: billingCycle === 'monthly' ? '#f8fafc' : '#64748b', fontWeight: 600, transition: 'color 0.3s', cursor: 'pointer' }} onClick={() => setBillingCycle('monthly')}>
                                Mensual
                            </Typography>
                            
                            <Switch 
                                checked={billingCycle === 'yearly'}
                                // CORRECCIÓN AQUÍ: AÑADIDO TIPO AL EVENTO 'e'
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBillingCycle(e.target.checked ? 'yearly' : 'monthly')}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#38bdf8' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#38bdf8' },
                                }}
                            />
                            
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" sx={{ color: billingCycle === 'yearly' ? '#f8fafc' : '#64748b', fontWeight: 600, transition: 'color 0.3s', cursor: 'pointer' }} onClick={() => setBillingCycle('yearly')}>
                                    Anual
                                </Typography>
                                <Chip label="2 MESES GRATIS" size="small" sx={{ bgcolor: '#fcd34d', color: '#0f172a', fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
                            </Stack>
                        </Stack>
                    </Box>
                    
                    <Grid container spacing={4} justifyContent="center" alignItems="flex-start">
                        
                        {/* Prueba gratuita de 30 dias, no es un plan permanente. */}
                        <Grid item xs={12} md={4}>
                            <RevealSection delay={0.1}>
                                <Paper elevation={0} sx={{ 
                                    p: 4, borderRadius: 4, height: '100%',
                                    bgcolor: alpha('#1e293b', 0.4), 
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid #334155',
                                    transition: 'transform 0.3s', 
                                    '&:hover': { transform: 'translateY(-10px)', border: '1px solid #94a3b8' } 
                                }}>
                                    <Typography variant="subtitle2" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                                        PRUEBA 30 DÍAS
                                    </Typography>
                                    <Box sx={{ my: 3, display: 'flex', alignItems: 'baseline' }}>
                                        <Typography variant="h3" fontWeight="800" color="#f8fafc">0€</Typography>
                                        <Typography variant="body1" color="#64748b" ml={1}>/30 días</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4, minHeight: '40px', lineHeight: 1.6 }}>
                                        Prueba PROAPricing durante 30 días, sin tarjeta ni cobro automático.
                                    </Typography>
                                    
                                    <Button 
                                        variant="outlined" fullWidth size="large" href="/signup"
                                        sx={{ 
                                            mb: 4, py: 1.5, borderColor: '#475569', color: '#cbd5e1', 
                                            '&:hover': { borderColor: '#f8fafc', color: '#f8fafc', bgcolor: 'rgba(255,255,255,0.05)' } 
                                        }}
                                    >
                                        Empezar prueba gratuita
                                    </Button>
                                    
                                    <Stack spacing={2}>
                                        {[
                                            'Carga de Datos Manual (CSV/Excel)', 
                                            'Auditoría Profit Leakage', 
                                            'Hasta 1.000 operaciones de optimización durante la prueba',
                                            'Acceso Business Intelligence',
                                            'Soporte Básico'
                                        ].map(item => (
                                            <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon sx={{ fontSize: 20, mr: 1.5, color: '#64748b' }} />
                                                <Typography variant="body2" color="#cbd5e1">{item}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>
                            </RevealSection>
                        </Grid>
                        
                        {/* TIER 2: GROWTH (The Main Engine - Highlighted) */}
                        <Grid item xs={12} md={4}>
                            <RevealSection delay={0.2}>
                                <Paper elevation={20} sx={{ 
                                    p: 4, borderRadius: 4, height: '100%',
                                    position: 'relative', 
                                    transform: { xs: 'none', md: 'scale(1.05)' }, 
                                    zIndex: 2,
                                    // Fondo con tinte Cyan muy sutil
                                    background: `linear-gradient(180deg, ${alpha('#1e293b', 0.9)} 0%, ${alpha('#0f172a', 0.95)} 100%)`,
                                    border: '1px solid #38bdf8',
                                    boxShadow: `0 0 40px -10px ${alpha('#38bdf8', 0.3)}`
                                }}>
                                    <Box sx={{ 
                                        position: 'absolute', top: -12, right: '50%', transform: 'translateX(50%)',
                                        bgcolor: '#38bdf8', color: '#0f172a', 
                                        px: 2, py: 0.5, borderRadius: 10, 
                                        fontWeight: 800, fontSize: '0.75rem', letterSpacing: 1,
                                        boxShadow: `0 0 15px ${alpha('#38bdf8', 0.6)}`
                                    }}>
                                        RECOMENDADO
                                    </Box>
                                    
                                    <Typography variant="subtitle2" sx={{ color: '#38bdf8', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                                        Growth
                                    </Typography>
                                    {/* PRECIO DINÁMICO GROWTH (990€ Base) */}
                                    <Box sx={{ my: 3, minHeight: '64px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                            <Typography variant="h3" fontWeight="800" color="#f8fafc">
                                                {billingCycle === 'monthly' ? '990€' : '825€'}
                                            </Typography>
                                            <Typography variant="body1" color="#94a3b8" ml={1}>/mes</Typography>
                                            
                                            {/* PRECIO TACHADO (SOLO ANUAL) */}
                                            {billingCycle === 'yearly' && (
                                                <Typography variant="body1" sx={{ ml: 2, color: '#64748b', textDecoration: 'line-through', fontWeight: 500 }}>
                                                    990€
                                                </Typography>
                                            )}
                                        </Box>
                                        
                                        {/* SUBTEXTO EXPLICATIVO */}
                                        <Typography variant="caption" sx={{ color: billingCycle === 'yearly' ? '#38bdf8' : 'transparent', display: 'block', mt: 0.5, fontWeight: 600 }}>
                                            {billingCycle === 'yearly' ? 'Facturado 9.900€ anualmente' : '\u00A0'} 
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 4, minHeight: '40px', lineHeight: 1.6 }}>
                                        Gestión dinámica y operativa masiva para equipos de alto rendimiento.
                                    </Typography>
                                    
                                    <Button 
                                        variant="contained" fullWidth size="large" href="/signup"
                                        sx={{
                                            mb: 4, py: 1.5, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800,
                                            '&:hover': { bgcolor: '#0ea5e9', boxShadow: `0 0 20px ${alpha('#38bdf8', 0.4)}` } 
                                        }}
                                    >
                                        Empezar Ahora
                                    </Button>
                                    
                                    <Stack spacing={2}>
                                        {[
                                            'Carga de Datos Automatizada', 
                                            'Optimizaciones Ilimitadas', 
                                            'Optimización por Lotes',
                                            'Entrenamiento Machine Learning Mensual', 
                                            'Predicción de Demanda (3 Meses)'
                                        ].map(item => (
                                            <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon sx={{ fontSize: 20, mr: 1.5, color: '#38bdf8' }} />
                                                <Typography variant="body2" color="#f8fafc" fontWeight="500">{item}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>
                            </RevealSection>
                        </Grid>

                         {/* TIER 3: AUTONOMY (The Empire) */}
                         <Grid item xs={12} md={4}>
                            <RevealSection delay={0.3}>
                                <Paper elevation={0} sx={{ 
                                    p: 4, borderRadius: 4, height: '100%',
                                    bgcolor: alpha('#1e293b', 0.4), 
                                    backdropFilter: 'blur(10px)',
                                    border: `1px solid ${alpha('#fcd34d', 0.3)}`, // Gold border
                                    transition: 'transform 0.3s', 
                                    '&:hover': { transform: 'translateY(-10px)', border: '1px solid #fcd34d', boxShadow: `0 0 30px -10px ${alpha('#fcd34d', 0.2)}` } 
                                }}>
                                    <Typography variant="subtitle2" sx={{ color: '#fcd34d', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                                        Autonomy
                                    </Typography>
                                    {/* PRECIO DINÁMICO AUTONOMY (1.490€ Base) */}
                                    <Box sx={{ my: 3, minHeight: '64px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                                            <Typography variant="h3" fontWeight="800" color="#f8fafc">
                                                {billingCycle === 'monthly' ? '1.490€' : '1.242€'}
                                            </Typography>
                                            <Typography variant="body1" color="#64748b" ml={1}>/mes</Typography>

                                            {/* PRECIO TACHADO (SOLO ANUAL) */}
                                            {billingCycle === 'yearly' && (
                                                <Typography variant="body1" sx={{ ml: 2, color: '#64748b', textDecoration: 'line-through', fontWeight: 500 }}>
                                                    1.490€
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* SUBTEXTO EXPLICATIVO */}
                                        <Typography variant="caption" sx={{ color: billingCycle === 'yearly' ? '#fcd34d' : 'transparent', display: 'block', mt: 0.5, fontWeight: 600 }}>
                                            {billingCycle === 'yearly' ? 'Facturado 14.900€ anualmente' : '\u00A0'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color: '#94a3b8', mb: 4, minHeight: '40px', lineHeight: 1.6 }}>
                                        Integración total con ERP y consultoría estratégica dedicada.
                                    </Typography>
                                    
                                    <Button 
                                        variant="outlined" fullWidth size="large" href="#contact-form"
                                        sx={{ 
                                            mb: 4, py: 1.5, borderColor: alpha('#fcd34d', 0.5), color: '#fcd34d', fontWeight: 600,
                                            '&:hover': { borderColor: '#fcd34d', bgcolor: alpha('#fcd34d', 0.1) } 
                                        }}
                                    >
                                        Contactar Ventas
                                    </Button>
                                    
                                    <Stack spacing={2}>
                                        {[
                                            'Conexión ERP',
                                            'Entrenamiento Machine Learning Semanal',
                                            'Predicción Estratégica (12 meses)', 
                                            'Consultor Asignado'
                                        ].map(item => (
                                            <Box key={item} sx={{ display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon sx={{ fontSize: 20, mr: 1.5, color: '#fcd34d' }} />
                                                <Typography variant="body2" color="#cbd5e1">{item}</Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Paper>
                            </RevealSection>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Divider sx={{ opacity: 0.1 }} />

            {/* =================================================================================
               6. CONTACT FORM (REDISEÑO DARK PREMIUM: LOGIN STYLE)
               ================================================================================= */}
            <Box id="contact-form" sx={{ py: { xs: 10, md: 16 }, bgcolor: '#0f172a' }}>
                <Container maxWidth="md">
                    <RevealSection>
                        <Paper sx={{ 
                            p: { xs: 4, md: 8 }, 
                            bgcolor: '#1e293b', // Fondo sólido Dark Premium
                            border: '1px solid #334155', // Borde sutil
                            borderRadius: 4,
                            boxShadow: '0 0 50px rgba(0, 0, 0, 0.5)' // Sombra profunda
                        }}>
                            <Box sx={{ textAlign: 'center', mb: 6 }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#f8fafc', mb: 2 }}>
                                    Hablemos de su Estrategia
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#94a3b8', fontSize: '1.1rem' }}>
                                    ¿Necesita una integración a medida o tiene grandes volúmenes de datos? <br />
                                    Nuestro equipo de ingeniería está listo para diseñar su solución.
                                </Typography>
                            </Box>

                            <Grid container spacing={3}> {/* Spacing ajustado a 3 para más cohesión */}
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Nombre Completo" name="name" variant="outlined" onChange={handleFormChange} sx={inputSx} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Email Corporativo" name="email" variant="outlined" onChange={handleFormChange} sx={inputSx} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField fullWidth label="Empresa" name="company" variant="outlined" onChange={handleFormChange} sx={inputSx} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField select fullWidth label="Interés Principal" name="tier" value={formState.tier} onChange={handleFormChange} sx={inputSx}>
                                        <MenuItem value="Enterprise">Plan Autonomy</MenuItem>
                                        <MenuItem value="Growth">Plan Growth</MenuItem>
                                        <MenuItem value="Other">Otro</MenuItem>
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth multiline rows={4} label="Mensaje" name="message" variant="outlined" placeholder="Cuéntenos brevemente su caso..." onChange={handleFormChange} sx={inputSx} />
                                </Grid>
                                
                                <Grid item xs={12} sx={{ textAlign: 'center', mt: 2 }}>
                                    <Button 
                                        variant="contained" 
                                        size="large" 
                                        endIcon={isSubmitting ? undefined : (submitStatus === 'success' ? <CheckCircleIcon /> : <SendIcon />)} 
                                        onClick={handleSubmit} 
                                        disabled={isSubmitting || submitStatus === 'success'}
                                        sx={{ 
                                            px: 6, py: 1.5, fontSize: '1.1rem', fontWeight: 800,
                                            bgcolor: submitStatus === 'success' ? '#4ade80' : '#38bdf8', // Verde éxito o Cian acción
                                            color: '#0f172a', // Texto oscuro para contraste
                                            '&:hover': { bgcolor: submitStatus === 'success' ? '#4ade80' : '#0ea5e9' },
                                            '&.Mui-disabled': { bgcolor: '#334155', color: '#64748b' }
                                        }}
                                    >
                                        {isSubmitting ? 'Enviando...' : (submitStatus === 'success' ? 'Solicitud Recibida' : 'Enviar Solicitud')}
                                    </Button>

                                    {submitStatus === 'error' && (
                                        <Typography color="error" variant="caption" display="block" sx={{ mt: 2, fontSize: '0.9rem' }}>
                                            Hubo un error. Por favor inténtelo de nuevo o escríbanos directamente.
                                        </Typography>
                                    )}

                                    <Box sx={{ mt: 5, pt: 4, borderTop: '1px solid #334155' }}>
                                        <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
                                            ¿Prefiere usar su cuenta de correo habitual?
                                        </Typography>
                                        <Stack 
                                            direction="row" alignItems="center" justifyContent="center" spacing={1} 
                                            sx={{ color: '#38bdf8', cursor: 'pointer', transition: 'color 0.2s', '&:hover': { color: '#7dd3fc' } }} 
                                            onClick={() => window.location.href = 'mailto:soporte@proapricing.ai'}
                                        >
                                            <EmailIcon fontSize="small" />
                                            <Typography fontWeight="600">soporte@proapricing.ai</Typography>
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    </RevealSection>
                </Container>
            </Box>

        </Box>
    );
};

export default LandingPage;